import { WhereItIs } from "../components/WhereItIs";
import { useBackState } from "@/hooks/useScrollMemory";
import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { fromState } from "@/lib/nav";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UserName } from "@/components/UserName";
import { InlinePickPanel } from "@/components/InlinePickPanel";
import { RETURN_REASONS } from "@/lib/returnReasons";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ScanField } from "@/components/ScanField";
import { useBuilderTrucks, exactBuilder } from "@/components/search/useBuilderTrucks";
import { useOwnTag } from "@/components/search/SearchBox";
import { AuthImg } from "@/components/AuthImg";
import { LinePhoto } from "@/components/LinePhoto";
import { NoPhotoBypassDialog, gapWords } from "@/components/NoPhotoBypass";
import { SendReasonDialog } from "@/components/SendReasonDialog";
import { scanAccept, scanRefuse, scanWarn, scanSoundOnly } from "@/lib/scanFeedback";
import { SoundToggle } from "@/components/SoundToggle";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  ShoppingCart, Plus, ScanLine, Search, Trash2, CheckCircle2, Wrench,
  Building2, Package, PackageCheck, DollarSign, Loader2, ClipboardList, AlertTriangle, Truck, ExternalLink, ShieldCheck,
  PiggyBank, Users, X, FileText, Undo2, ChevronDown, Merge, Camera, Receipt, HandMetal,
} from "lucide-react";

const STATUS_PILL = {
  draft: "bg-slate-100 text-slate-700", approved: "bg-blue-100 text-blue-700",
  in_pick: "bg-amber-100 text-amber-700", completed: "bg-green-100 text-green-700",
  signed_off: "bg-emerald-600 text-white", cancelled: "bg-red-100 text-red-700",
};
const LINE_PILL = {
  draft: "text-slate-500", reserved: "text-blue-600", picked: "text-amber-600",
  removed: "text-green-600", short: "text-red-600",
  shipped: "text-emerald-600", returned: "text-sky-600",
};
// A shipped line's raw state is "removed" (left the yard) — relabel it so the floor reads it right.
function lineStateView(l) {
  if (l.returned) return { key: "returned", label: "returned" };
  if (l.shipment_number && l.state === "removed") return { key: "shipped", label: "shipped" };
  return { key: l.state, label: l.state };
}

function money(v) { return v == null ? "—" : `$${Number(v).toFixed(2)}`; }
function assignmentShort(c) {
  if (c.assignment_type === "build") return `Build · PO ${c.build?.po_number || "—"}`;
  if (c.assignment_type === "customer_order") {
    const co = c.customer_order || {};
    return `Customer${co.customer_name ? ` · ${co.customer_name}` : ""}${co.walk_in ? " · walk-in" : ""}`;
  }
  return "Internal Use";
}
function fmtWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d) ? String(iso).slice(0, 16).replace("T", " ")
    : d.toLocaleString(undefined, { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
}

// ONE COLOR LANGUAGE (shared across cart buttons, cart stepper, transfers):
//   GREEN = go / affirmative / done (Approve, Ship, Complete, Sign-off, received)
//   RED   = destructive / terminal (Void, Scrap)
//   BLUE  = transfer / relocate identity
//   DARK  = navigate / secondary (Go to Picking)
//   AMBER = caution / needs-attention / must-do ONLY (never a primary action)
const STEP_ORDER = ["created", "approved", "picking", "shipping", "completed", "signed_off"];

function cartCurrentStage(cart) {
  const lines = cart.lines || [];
  const removed = lines.filter((l) => l.state === "removed").length;
  const open = lines.filter((l) => l.state === "reserved" || l.state === "picked").length;
  const s = cart.status;
  let current;
  if (s === "cancelled") current = "cancelled";
  else if (s === "signed_off") current = "signed_off";
  else if (s === "completed") current = "completed";
  else if (removed > 0 && open > 0) current = "shipping";  // PARTIAL — the honest middle state
  else if (s === "draft") current = "created";
  else current = "picking";                                 // approved/in_pick, nothing shipped yet
  return { current, removed, open };
}

// Connected stage stepper: done=solid green, current=green pulsing, pending=grey, Void=red terminal.
// Teaches green crew where-they-are-in-the-sequence while keeping the who/when accountability trail.
function CartTrail({ cart, shipments = [] }) {
  const lines = cart.lines || [];
  const uniq = (a) => [...new Set(a.filter(Boolean))];
  const pk = lines.filter((l) => l.picked_by);
  const rm = lines.filter((l) => l.removed_by);
  const { current, removed, open } = cartCurrentStage(cart);
  const lastAt = (arr) => arr.map((l) => l).filter(Boolean).sort().pop();

  let nodes;
  if (current === "cancelled") {
    nodes = [
      { key: "created", icon: Plus, label: "Created", bys: [cart.created_by], at: cart.created_at, state: "done" },
      { key: "approved", icon: CheckCircle2, label: "Approved · reserved", bys: [cart.approved_by], at: cart.approved_at, state: cart.approved_at ? "done" : "skipped" },
      { key: "cancelled", icon: Trash2, label: "Voided", bys: [], at: cart.updated_at, state: "terminal" },
    ];
  } else {
    const curIdx = STEP_ORDER.indexOf(current);
    const base = [
      { key: "created", icon: Plus, label: "Created", bys: [cart.created_by], at: cart.created_at },
      { key: "approved", icon: CheckCircle2, label: "Approved · reserved", bys: [cart.approved_by], at: cart.approved_at },
      { key: "picking", icon: ScanLine, label: pk.length ? `Picked · ${pk.length}` : "Picking", bys: uniq(pk.map((l) => l.picked_by)), at: lastAt(pk.map((l) => l.picked_at)) },
      { key: "shipping", icon: Truck,
        label: (removed > 0 && open > 0) ? `Shipping · ${removed} sent · ${open} open`
              : removed > 0 ? `Shipped · ${removed}` : "Shipping",
        sub: shipments.length ? `${shipments.length} shipment${shipments.length > 1 ? "s" : ""} sent` : null,
        bys: uniq(rm.map((l) => l.removed_by)), at: lastAt(rm.map((l) => l.removed_at)) },
      { key: "completed", icon: PackageCheck, label: "Completed", bys: [], at: cart.completed_at },
      { key: "signed_off", icon: ShieldCheck, label: "Signed off · Foreman", bys: [cart.signed_off_by], at: cart.signed_off_at },
    ];
    nodes = base.map((n) => {
      const i = STEP_ORDER.indexOf(n.key);
      return { ...n, state: i < curIdx ? "done" : i === curIdx ? "current" : "pending" };
    });
  }

  const dot = (st) => st === "done" ? "bg-emerald-600 text-white"
    : st === "current" ? "bg-emerald-600 text-white ring-4 ring-emerald-200 animate-pulse"
    : st === "terminal" ? "bg-[#B01E28] text-white"
    : "bg-slate-200 text-slate-400";
  const releases = (cart.history || []).filter((h) => h.note && /released/i.test(h.note));

  return (
    <div data-testid="cart-trail" className="border border-slate-200 rounded-lg bg-slate-50/60 p-3 mb-4">
      <div className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mb-3">Progress · accountability trail</div>
      <div className="flex items-center overflow-x-auto pb-1">
        {nodes.map((s, idx) => (
          <div key={s.key} data-testid={`trail-${s.key}`} className={`flex items-center ${idx < nodes.length - 1 ? "flex-1 min-w-[64px]" : "shrink-0"} ${s.state === "pending" || s.state === "skipped" ? "opacity-45" : ""}`}>
            <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${dot(s.state)}`}><s.icon className="w-4 h-4" /></div>
            {idx < nodes.length - 1 && <div className={`h-0.5 flex-1 mx-1 rounded ${s.state === "done" ? "bg-emerald-500" : s.state === "terminal" ? "bg-[#B01E28]" : "bg-slate-200"}`} />}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-4 gap-y-2 mt-2">
        {nodes.map((s) => (
          <div key={`lbl-${s.key}`} className={`text-xs leading-tight ${s.state === "pending" ? "opacity-45" : ""}`}>
            <div className={`font-semibold ${s.state === "current" ? "text-emerald-700" : s.state === "terminal" ? "text-[#B01E28]" : "text-slate-700"}`}>{s.label}</div>
            {s.sub && <div className="text-[10px] text-emerald-600 font-mono">{s.sub}</div>}
            <div className="text-slate-500">{(s.state === "done" || s.state === "current" || s.state === "terminal")
              ? <>{(s.bys && s.bys.filter(Boolean).length)
                    ? s.bys.filter(Boolean).map((e, i) => <span key={e}>{i > 0 ? ", " : ""}<UserName email={e} className="font-medium" /></span>)
                    : <span className="text-slate-400">—</span>}{s.at ? <span className="text-slate-400"> · {fmtWhen(s.at)}</span> : null}</>
              : <span className="text-slate-400">{s.state === "skipped" ? "skipped" : "pending"}</span>}</div>
          </div>
        ))}
      </div>
      {releases.length > 0 && (
        <div className="mt-2 pt-2 border-t border-slate-200 text-[11px] text-slate-500 space-y-0.5">
          {releases.map((h, i) => <div key={i} data-testid="trail-release">↩ {h.note} · <span className="font-medium"><UserName email={h.by} /></span> · {fmtWhen(h.at)}</div>)}
        </div>
      )}
    </div>
  );
}

// Increment 4: Pricing Review Queue — unpriced-at-checkout parts, TWO separated buckets.
// CASH (sold, needs value_out → recovery) is money-urgent; REF (build/internal OEM ref →
// avoided cost) is a lower-stakes data gap. Never blocks the floor — this is post-checkout.
function GapRow({ cartId, gap, kind, canPrice, onPrice }) {
  const [v, setV] = useState("");
  return (
    <div data-testid={`gap-${kind}-${gap.short_code}`} className="flex items-center gap-2 px-3 py-2 border-b border-slate-100 text-sm">
      {gap.photo ? <AuthImg fileId={gap.photo} thumb className="w-9 h-9 rounded object-cover border border-slate-200" alt={gap.short_code} />
        : <div className="w-9 h-9 rounded bg-slate-100 flex items-center justify-center"><Package className="w-4 h-4 text-slate-400" /></div>}
      <div className="min-w-0 flex-1">
        <span className="font-mono font-bold">{gap.short_code}</span> <span className="text-slate-500 text-xs">{gap.name}</span>
      </div>
      {canPrice ? (
        <div className="flex items-center gap-1 shrink-0">
          <span className="text-slate-400 text-xs">$</span>
          <input data-testid={`gap-input-${gap.short_code}`} type="number" value={v} onChange={(e) => setV(e.target.value)}
            className="w-24 border border-slate-200 rounded-lg px-2 py-1 text-sm font-mono" placeholder={kind === "cash" ? "sale price" : "OEM ref"} />
          <Button data-testid={`gap-save-${gap.short_code}`} size="sm" className="rounded-lg h-8" disabled={!v}
            onClick={() => { onPrice(cartId, gap.part_id, kind, v); setV(""); }}>Save</Button>
        </div>
      ) : <span className="text-xs text-slate-400 shrink-0">needs price</span>}
    </div>
  );
}

function PricingQueue({ queue, canPrice, canSignOff, onPrice, onSignOff }) {
  if (!queue) return <div className="p-6 text-center text-sm text-slate-400" data-testid="pricing-queue-loading"><Loader2 className="w-5 h-5 mx-auto animate-spin" /></div>;
  if (!queue.carts.length) return (
    <div className="p-10 text-center text-sm text-slate-400" data-testid="pricing-queue-empty">
      <CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-emerald-300" />Nothing waiting to be priced. All checked-out parts are valued.
    </div>
  );
  return (
    <div className="space-y-4" data-testid="pricing-queue">
      <div className="flex flex-wrap gap-4 text-xs">
        <span className="inline-flex items-center gap-1.5 font-semibold text-[#B01E28]"><DollarSign className="w-4 h-4" />{queue.cash_parts} sold part(s) — cash owed to recovery</span>
        <span className="inline-flex items-center gap-1.5 text-slate-500"><AlertTriangle className="w-4 h-4" />{queue.ref_parts} build/internal — missing OEM reference</span>
      </div>
      {queue.carts.map((c) => (
        <div key={c.id} data-testid={`queue-cart-${c.cart_number}`} className="border border-slate-200 rounded-lg">
          <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 bg-slate-50 border-b border-slate-200">
            <div className="flex items-center gap-2">
              <span className="font-mono font-bold text-sm">{c.cart_number}</span>
              <span className="text-[11px] text-slate-500">{assignmentShort(c)}</span>
            </div>
            {canSignOff && (
              <Button data-testid={`queue-signoff-${c.cart_number}`} size="sm"
                disabled={c.cash_gaps.length > 0}
                className="rounded-lg h-8 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40"
                onClick={() => onSignOff(c.id)}>
                <ShieldCheck className="w-4 h-4 mr-1" />{c.cash_gaps.length > 0 ? "Price sold parts to sign off" : "Sign off"}
              </Button>
            )}
          </div>
          {c.cash_gaps.length > 0 && (
            <div data-testid={`queue-cash-${c.cart_number}`}>
              <div className="px-3 py-1.5 bg-red-50 border-b border-red-100 font-mono text-[10px] uppercase tracking-widest text-[#B01E28]">Cash — needs sale price ({c.cash_gaps.length})</div>
              {c.cash_gaps.map((g) => <GapRow key={g.part_id} cartId={c.id} gap={g} kind="cash" canPrice={canPrice} onPrice={onPrice} />)}
            </div>
          )}
          {c.ref_gaps.length > 0 && (
            <div data-testid={`queue-ref-${c.cart_number}`}>
              <div className="px-3 py-1.5 bg-amber-50 border-b border-amber-100 font-mono text-[10px] uppercase tracking-widest text-amber-700">Avoided-cost — missing OEM reference ({c.ref_gaps.length})</div>
              {c.ref_gaps.map((g) => <GapRow key={g.part_id} cartId={c.id} gap={g} kind="ref" canPrice={canPrice} onPrice={onPrice} />)}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function PullCartsPage() {
  const { has } = useAuth();
  const [carts, setCarts] = useState([]);
  const [sel, setSel] = useState(null);
  const [creating, setCreating] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  // new cart form
  const [atype, setAtype] = useState("build");
  const [buildName, setBuildName] = useState("");
  const [jobName, setJobName] = useState("");            // EVERY JOB IS NAMED
  const [builderQ, setBuilderQ] = useState("");          // type-to-find the truck
  const [builderOpen, setBuilderOpen] = useState(false);
  const [openJobAsk, setOpenJobAsk] = useState(null);    // {cart} — "already has an open job — add to it?"
  const [dupes, setDupes] = useState(null);              // duplicate open jobs, waiting to be merged
  const [mergeAsk, setMergeAsk] = useState(null);        // {group, into, from[]} pending confirm
  const [merging, setMerging] = useState(false);
  const [owed, setOwed] = useState(null);                // parts that left without a tag or photo
  const [owedOpen, setOwedOpen] = useBackState("owedOpen", false);
  const [renaming, setRenaming] = useState(false);
  const [renameTo, setRenameTo] = useState("");
  const [showOwed, setShowOwed] = useBackState("showOwed", false);
  const [handedTo, setHandedTo] = useState("");          // the handover stamp
  const [handNote, setHandNote] = useState("");
  const [po, setPo] = useState("");
  const [builder, setBuilder] = useState(null);   // the picked builder truck
  const builderId = builder?.id || "";
  const [poMatch, setPoMatch] = useState(null);  // reverse-link: builder that owns a typed PO
  const [notes, setNotes] = useState("");
  const [custName, setCustName] = useState("");
  const [walkIn, setWalkIn] = useState(false);
  const [orderRef, setOrderRef] = useState("");
  const [custPo, setCustPo] = useState("");
  const [contact, setContact] = useState("");
  const [otherWarn, setOtherWarn] = useState(null); // {kind, carts|cart, short_code, onConfirm}
  const [lastAdded, setLastAdded] = useState(null); // green scan-confirm flash
  const [showQueue, setShowQueue] = useBackState("showQueue", false);
  const [queue, setQueue] = useState(null); // {count, cash_parts, ref_parts, carts:[]}
  const [signBlock, setSignBlock] = useState(null); // {count, parts} when sign-off blocked
  const [fieldAdd, setFieldAdd] = useState(null); // {short_code, gaps, onConfirm} field-add confirm
  const [showCompletion, setShowCompletion] = useBackState("showCompletion", false);
  const [completion, setCompletion] = useState(null); // {count, parts, carts:[]}
  const [completionBlock, setCompletionBlock] = useState(null); // {count, parts} when sign-off blocked on ID
  const [notReadyBlock, setNotReadyBlock] = useState(null); // {count, parts:[{short_code,name,missing}]} — strict sign-off
  const [shipSel, setShipSel] = useState([]);       // selected part_id
  const [shipAsk, setShipAsk] = useState(null);              // ONE box: every bin scan, tag and photo a send is missing
  const [noPhoto, setNoPhoto] = useState(null);              // going ON the job without its tag or photo — same rule
  const [shipSheet, setShipSheet] = useState(false); // confirm sheet open
  const [shipName, setShipName] = useState("");
  const [shipping, setShipping] = useState(false);
  const [shipments, setShipments] = useState([]);    // this cart's shipment history

  const canBuild = has("build_cart"), canPrice = has("set_pricing"), canApprove = has("approve_cart"), canSignOff = has("sign_off_cart"), canShip = has("ship_parts"), canReturn = has("process_return");
  const canPick = has("pick_remove");
  const navigate = useNavigate();
  const location = useLocation();
  const [removeSel, setRemoveSel] = useState([]);
  const [returnSel, setReturnSel] = useState([]);
  const [returnConfirm, setReturnConfirm] = useState(null);   // {ids} pending confirmation
  const [discardConfirm, setDiscardConfirm] = useState(false);
  const [signoffConfirm, setSignoffConfirm] = useState(false);
  const [returnReason, setReturnReason] = useState(null);
  const [returnNote, setReturnNote] = useState("");
  const [returning, setReturning] = useState(false);
  const [shortPickL, setShortPickL] = useState(null);
  const [shortReason, setShortReason] = useState("");
  const [shortBusy, setShortBusy] = useState(false);
  const [cartQ, setCartQ] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchHasMore, setSearchHasMore] = useState(false);
  const [termPage, setTermPage] = useState(1);
  const [termTotal, setTermTotal] = useState(0);
  const [termHasMore, setTermHasMore] = useState(false);

  // THE TWO TICKETS — printed on request, never automatically. Retail pricing only.
  const openTicket = async (cart, which, shipment) => {
    try {
      const { data } = await api.get(`/carts/${cart.id}/${which}`, {
        params: shipment ? { shipment } : undefined, responseType: "text" });
      const url = URL.createObjectURL(new Blob([data], { type: "text/html" }));
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 120000);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not open the ticket"); }
  };

  const loadQueue = useCallback(async () => {
    try {
      // Page through completed carts (server paginates) and assemble the full pricing
      // backlog — bounded DB scan per request, N+1 removed server-side, nothing dropped.
      let page = 1; const carts = []; let cashParts = 0, refParts = 0; let week = {}; let first = true;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { data } = await api.get("/carts/pricing-queue", { params: { page, page_size: 100 } });
        if (first) { week = data.week || {}; first = false; }
        carts.push(...(data.carts || []));
        cashParts += data.cash_parts || 0;
        refParts += data.ref_parts || 0;
        if (!data.has_more || page > 500) break;
        page += 1;
      }
      setQueue({ count: carts.length, cash_parts: cashParts, ref_parts: refParts, week, carts });
    }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, []);
  const loadCompletion = useCallback(async () => {
    try { const { data } = await api.get("/carts/completion-queue"); setCompletion(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, []);

  const loadCarts = useCallback(async () => {
    try {
      // ACTIVE carts (draft/approved/in_pick) are ALWAYS loaded in full — page through
      // every one so an open/active cart can NEVER be hidden by age (Finding #1 fix).
      const active = []; let p = 1;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { data } = await api.get("/carts", { params: { statuses: "draft,approved,in_pick", page: p, page_size: 200 } });
        active.push(...(data.items || []));
        if (!data.has_more || p > 100) break;
        p += 1;
      }
      // TERMINAL history (completed/signed_off/cancelled) is paginated with a visible
      // "Load more" — never a silent cutoff. Page 1 here; user pulls more on demand.
      const { data: term } = await api.get("/carts", { params: { statuses: "completed,signed_off,cancelled", page: 1, page_size: 50 } });
      setCarts([...active, ...(term.items || [])]);
      setTermPage(1); setTermTotal(term.total || 0); setTermHasMore(!!term.has_more);
    }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, []);
  const loadMoreTerminal = useCallback(async () => {
    const next = termPage + 1;
    try {
      const { data } = await api.get("/carts", { params: { statuses: "completed,signed_off,cancelled", page: next, page_size: 50 } });
      setCarts((prev) => [...prev, ...(data.items || [])]);
      setTermPage(next); setTermHasMore(!!data.has_more);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, [termPage]);
  const doMerge = async () => {
    if (!mergeAsk) return;
    setMerging(true);
    try {
      const { data } = await api.post(`/carts/${mergeAsk.into.id}/merge`,
        { from_cart_ids: mergeAsk.from.map((j) => j.id), confirm: true });
      toast.success(`${data.moved} part(s) moved into ${mergeAsk.into.cart_number}`);
      setMergeAsk(null);
      await loadCarts(); await loadDupes();
      setSearchParams({ open: mergeAsk.into.id });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setMerging(false);
  };

  const loadCart = useCallback(async (id) => {
    try { const { data } = await api.get(`/carts/${id}`); setSel(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, []);
  // SERVER-SIDE cart search (debounced): queries every cart by #/PO/build/customer regardless
  // of what's loaded — an older cart is never invisible just because it fell off a page.
  useEffect(() => {
    const term = cartQ.trim();
    if (!term) { setSearchResults(null); setSearchHasMore(false); setSearchLoading(false); return; }
    setSearchLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/carts", { params: { q: term, page: 1, page_size: 200 } });
        setSearchResults(data.items || []);
        setSearchHasMore(!!data.has_more);
      } catch { setSearchResults([]); setSearchHasMore(false); }
      finally { setSearchLoading(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [cartQ]);
  const loadDupes = useCallback(async () => {
    try { const { data } = await api.get("/carts/duplicates"); setDupes(data); } catch { setDupes(null); }
  }, []);
  const loadOwed = useCallback(async () => {
    try { const { data } = await api.get("/carts/photo-owed"); setOwed(data); } catch { setOwed(null); }
  }, []);
  const loadShipments = useCallback(async (id) => {
    try { const { data } = await api.get(`/carts/${id}/shipments`); setShipments(data.shipments || []); }
    catch { setShipments([]); }
  }, []);

  useEffect(() => { loadCarts(); }, [loadCarts]);
  useEffect(() => { if (canBuild) { loadDupes(); loadOwed(); } }, [canBuild, loadDupes, loadOwed]);
  // Back to this page: a list he had open stays open, asked for fresh
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (showQueue) loadQueue(); if (showCompletion) loadCompletion(); }, []);
  const [awaiting, setAwaiting] = useState(null);
  useEffect(() => { api.get("/shipments/stats").then(({ data }) => setAwaiting(data.awaiting_next_truck)).catch(() => {}); }, [carts]);
  useEffect(() => {
    if (sel?.id) { loadShipments(sel.id); setShipSel([]); }
    else setShipments([]);
  }, [sel?.id, sel?.status, loadShipments]);
  // Deep-link: /carts?open=<cartId> auto-opens that cart (from a part's "View" link).
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const openId = searchParams.get("open");
    // Keep the open cart in the URL so a round-trip (part -> back, or complete -> save) returns
    // EXACTLY to this cart. Guarded so it doesn't re-load in a loop once sel matches.
    if (openId && openId !== sel?.id) loadCart(openId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const openCart = (id) => setSearchParams({ open: id });
  const builderRes = useBuilderTrucks(builderQ, canBuild && builderOpen && !builderId, { box: "cart_which_truck" });
  const builderHits = builderRes.items;

  // Choosing a builder AUTO-POPULATES + locks its PO (the truck is the authoritative home).
  const selectBuilder = (t) => {
    setBuilder(t || null);
    setPoMatch(null);
    setPo(t ? (t.po_number || "") : "");
  };
  // ENTER (S4, his rule): a builder truck's own stock # typed exactly picks it; anything else stays a list.
  const ownTag = useOwnTag("build-builder-search");
  const builderEnter = async (v) => {
    const t = await exactBuilder(await ownTag((v || "").trim()));
    if (t) { selectBuilder(t); setBuilderOpen(false); }
  };

  // Reverse-link: a manually-typed PO (no builder chosen) that matches a builder
  // truck's PO surfaces that builder so the two connect from the PO side.
  useEffect(() => {
    if (!canBuild || builderId || !po.trim()) { setPoMatch(null); return; }
    const h = setTimeout(() => {
      api.get(`/donors/by-po`, { params: { po } }).then(({ data }) => setPoMatch(data?.match || null)).catch(() => setPoMatch(null));
    }, 350);
    return () => clearTimeout(h);
  }, [po, builderId, canBuild]);

  // ONE PO = ONE JOB. Before a second job is started for the same PO / truck / order, ask —
  // with YES as the default, because adding to the open job is nearly always what he means.
  const createCart = async (skipCheck) => {
    if (!skipCheck) {
      try {
        const params = atype === "build"
          ? { assignment_type: "build", po: po, builder_id: builderId || undefined }
          : atype === "customer_order"
            ? { assignment_type: "customer_order", ref: orderRef || custPo || undefined, customer: custName || undefined }
            : null;
        if (params && (params.po || params.builder_id || params.ref || params.customer)) {
          const { data } = await api.get("/carts/open-job", { params });
          if (data.found) { setOpenJobAsk(data.cart); return; }
        }
      } catch { /* the question is a courtesy — never a blocker */ }
    }
    setOpenJobAsk(null);
    const payload = { assignment_type: atype, name: jobName };
    if (atype === "build") payload.build = { name: jobName || buildName, po_number: po, builder_id: builderId || null };
    if (atype === "customer_order") payload.customer_order = { customer_name: custName, walk_in: walkIn, order_ref: orderRef, po_number: custPo, contact, notes };
    if (atype === "internal_use") payload.internal_use = { purpose: notes };
    try {
      const { data } = await api.post("/carts", payload);
      toast.success(`${data.cart_number} created`);
      setCreating(false); setBuildName(""); setJobName(""); setPo(""); setBuilder(null); setBuilderQ(""); setPoMatch(null); setNotes("");
      setCustName(""); setWalkIn(false); setOrderRef(""); setCustPo(""); setContact("");
      await loadCarts(); setSel(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const tryAdd = async (payload) => {
    try {
      const { data } = await api.post(`/carts/${sel.id}/add-part`, payload);
      setSel(data); scanAccept((data.lines || []).slice(-1)[0]?.short_code || "ON THE JOB");
      const lns = data.lines || [];
      const added = lns[lns.length - 1] || null;
      setLastAdded(added);
      if (added?.added_without_tag_or_photo) toast.success(`${added.short_code} added — no ${gapWords(added.added_without_tag_or_photo.missing)} yet, reason logged. Still needed before sign-off.`);
      else if (added?.field_added) toast(`${added.short_code} added — needs ID later (moved on tag + photo)`, { icon: "📋" });
      return true;
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d && typeof d === "object" && d.code === "on_other_cart") {
        setOtherWarn({ kind: "draft", carts: d.carts || [], short_code: d.short_code, onConfirm: () => tryAdd({ ...payload, force: true }) });
        scanSoundOnly(false); return false;   // the dialog opening IS the plain words — no tint over it
      }
      if (d && typeof d === "object" && d.code === "reserved_elsewhere") {
        const relPayload = payload.part_id ? { part_id: payload.part_id } : { part_scan: payload.part_scan };
        setOtherWarn({ kind: "reserved", cart: d.cart, short_code: d.short_code,
          onConfirm: canApprove ? async () => { await api.post("/carts/release-part", relPayload); return tryAdd(payload); } : null });
        scanSoundOnly(false); return false;
      }
      if (d && typeof d === "object" && d.code === "needs_field_add") {
        setFieldAdd({ short_code: d.short_code, gaps: d.gaps || [], onConfirm: () => tryAdd({ ...payload, field_add: true }) });
        scanSoundOnly(false); return false;
      }
      if (d && typeof d === "object" && d.code === "already_on_this_job") {
        scanWarn(d.message); return false;   // not wrong, just not new — amber, never red
      }
      if (d && typeof d === "object" && d.code === "field_add_blocked" && d.override_available) {
        setNoPhoto({ ...d, payload }); scanSoundOnly(false); return false;   // the dialog IS the plain words
      }
      scanRefuse(formatApiErrorDetail(d)); toast.error(formatApiErrorDetail(d)); return false;
    }
  };
  const addByScan = async (code) => { if (code && sel) await tryAdd({ part_scan: code }); };
  const doSearch = async (e) => {
    e.preventDefault();
    const term = q.trim();
    try {
      const { data } = await api.get("/search", { params: { q, scope: "parts", status: "available", page_size: 12, box: "cart_add_part" } });
      setResults(data.parts || []);
      // a typed TAG that is not on the shelf yet (a child just made, a part off a truck) adds like a scan
      if (!(data.parts || []).length && /^[a-z]{2,4}-?\d{3,}$/i.test(term)) await addByScan(term);
    }
    catch (e) { toast.error("The search didn't go through — check the signal and tap Go again."); }
  };
  const addById = async (pid) => { if (await tryAdd({ part_id: pid })) setResults((r) => r.filter((p) => p.id !== pid)); };
  const removeLine = async (pid) => {
    try { const { data } = await api.delete(`/carts/${sel.id}/lines/${pid}`); setSel(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const setPrice = async (pid, retail) => {
    try { const { data } = await api.post(`/carts/${sel.id}/price`, { part_id: pid, retail_price: parseFloat(retail) }); setSel(data); toast.success("Price updated"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  // the foreman ticks the price he checked — person-entered, never automatic
  const doRename = async () => {
    const nm = renameTo.trim();
    if (!nm) { toast.error("Give the job a name."); return; }
    try {
      const { data } = await api.patch(`/carts/${sel.id}`, { name: nm });
      setSel(data); setRenaming(false); await loadCarts();
      toast.success("Renamed");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const checkPrice = async (pid) => {
    try { const { data } = await api.post(`/carts/${sel.id}/price`, { part_id: pid, check_only: true }); setSel(data); toast.success("Price ticked as checked"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const approve = async () => {
    try {
      const { data } = await api.post(`/carts/${sel.id}/approve`);
      const n = data.notification?.status;
      toast.success(`${data.cart_number} approved — parts reserved`, { description: n === "sent" ? "Warehouse emailed." : "Warehouse notification queued (add Resend key to send live)." });
      setSel(data); await loadCarts();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  // Inline pick helpers (field-added one-tap pick + can't-find short-pick) — same endpoints
  // as the pull station, so gates are identical no matter which surface.
  const pickLine = async (l) => {
    const token = l.qr_token || l.barcode_token || l.tag_code || l.short_code;
    try {
      const { data } = await api.post(`/carts/${sel.id}/pick`, { part_scan: token });
      if (data.ok) { toast.success(`Picked ${l.short_code}`); await loadCart(sel.id); }
      else toast.error(data.message);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const shortPick = (l) => { setShortPickL(l); setShortReason(""); };
  const SHORT_PRESETS = ["Not on the shelf", "Wrong location", "Damaged / unusable", "Already gone"];
  const submitShortPick = async () => {
    const note = shortReason.trim();
    if (note.length < 3 || !shortPickL) return;
    setShortBusy(true);
    try {
      await api.post(`/carts/${sel.id}/short-pick`, { part_id: shortPickL.part_id, note });
      toast.message("Flagged short — line left unfilled, part released.");
      setShortPickL(null); await loadCart(sel.id); await loadCarts();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setShortBusy(false); }
  };
  const toggleRemove = (pid) => setRemoveSel((s) => s.includes(pid) ? s.filter((x) => x !== pid) : [...s, pid]);
  const toggleReturn = (pid) => setReturnSel((s) => s.includes(pid) ? s.filter((x) => x !== pid) : [...s, pid]);
  const doReturn = (partIds) => {
    const ids = Array.isArray(partIds) ? partIds : [partIds];
    if (!ids.length) return;
    setReturnReason(null); setReturnNote("");
    setReturnConfirm({ ids });
  };
  const confirmReturn = async () => {
    const ids = returnConfirm?.ids || [];
    if (!ids.length || !returnReason) { return; }
    if (returnReason === "other" && returnNote.trim().length < 2) return;
    setReturning(true);
    try {
      const { data } = await api.post(`/carts/${sel.id}/return-lines`, {
        part_ids: ids, reason: returnReason, reason_note: returnReason === "other" ? returnNote.trim() : "" });
      toast.success(`Returned ${data.returned} part(s) to put-away`);
      setReturnSel([]); setReturnConfirm(null); setReturnReason(null); setReturnNote("");
      await loadCart(sel.id); await loadCarts();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setReturning(false); }
  };
  const discardDraft = async () => {
    if (!sel) return;
    setDiscardConfirm(false);
    try {
      await api.delete(`/carts/${sel.id}`);
      toast.success("Draft discarded");
      setSel(null); setSearchParams({}); await loadCarts();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const bulkRemove = async () => {
    if (!removeSel.length) return;
    try {
      await api.post(`/carts/${sel.id}/remove-lines`, { part_ids: removeSel });
      toast.success(`Removed ${removeSel.length} part(s) from the build/order`);
      setRemoveSel([]); await loadCart(sel.id); await loadCarts();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const [voidConfirm, setVoidConfirm] = useState(false);
  const [voiding, setVoiding] = useState(false);
  const doVoid = async () => {
    setVoiding(true);
    try { const { data } = await api.post(`/carts/${sel.id}/cancel`); setSel(data); await loadCarts(); toast.success(`${data.cart_number} voided — cart emptied`); setVoidConfirm(false); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setVoiding(false); }
  };

  // Increment 4: retroactive pricing on a removed part (cash flows to recovery / OEM ref for avoided cost)
  const priceRemoved = async (cartId, partId, kind, value) => {
    const v = parseFloat(value);
    if (isNaN(v)) return;
    const body = kind === "cash" ? { part_id: partId, value_out: v } : { part_id: partId, oem_new_reference_price: v };
    try {
      await api.post(`/carts/${cartId}/price-removed`, body);
      toast.success(kind === "cash" ? `Sale price set — $${v.toFixed(2)} flows to recovery` : `OEM reference set — $${v.toFixed(2)}`);
      await loadQueue();
      if (sel?.id === cartId) await loadCart(cartId);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  // Increment 4: Foreman sign-off — hard-blocked on unpriced sold parts
  const signOff = async (cartId) => {
    try {
      const { data } = await api.post(`/carts/${cartId}/sign-off`);
      toast.success(`${data.cart_number} signed off — cart fully closed`);
      setSignBlock(null); await loadCarts(); await loadQueue();
      if (sel?.id === cartId) setSel(data);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d && typeof d === "object" && d.code === "unpriced_cash") { setSignBlock(d); }
      else if (d && typeof d === "object" && d.code === "needs_completion") { setCompletionBlock(d); }
      else if (d && typeof d === "object" && d.code === "not_ship_ready") { setNotReadyBlock(d); }
      else toast.error(formatApiErrorDetail(d));
    }
  };

  // ONE ANSWER PER SEND: the reason covers every missing bin scan, tag and photo at once, so the send is never
  // asked twice — and a send that already carried a reason is never asked again (it says what's wrong instead).
  const doShip = async (reason) => {
    if (!sel?.id || !shipSel.length) return;
    setShipping(true);
    try {
      const body = { part_ids: shipSel, name: shipName || null };
      if (handedTo.trim()) { body.handed_to = handedTo.trim(); body.handover_note = handNote.trim() || null; }
      if (reason) Object.assign(body, { bin_override: true, override_reason: reason, floor_override: true, floor_override_reason: reason });
      const { data } = await api.post(`/carts/${sel.id}/ship`, body);
      scanAccept(`${data.shipment.shipment_number} SENT`);
      toast.success(`${data.shipment.shipment_number} sent · ${data.shipment.part_count} part(s)${reason ? " (what was missing is logged on each part)" : ""}${data.completed ? " — cart complete" : " — cart stays open"}`);
      setShipAsk(null); setShipSheet(false); setShipName(""); setShipSel([]); setHandedTo(""); setHandNote("");
      await loadOwed();
      setSel(data.cart); await loadShipments(sel.id); await loadCarts();
    } catch (e) {
      const d = e.response?.data?.detail;
      const gate = d && typeof d === "object" && (d.code === "ship_floor_blocked" || d.code === "pick_required");
      if (gate && d.override_available && !reason) { setShipSheet(false); setShipAsk(d); }
      else { setShipAsk(null); toast.error(gate ? `${d.message} (${(d.everything || d.parts || []).map((p) => p.short_code).join(", ")})` : formatApiErrorDetail(d)); }
    } finally { setShipping(false); }
  };
  const toggleShip = (pid) => setShipSel((s) => s.includes(pid) ? s.filter((x) => x !== pid) : [...s, pid]);

  const isDraft = sel?.status === "draft";
  const cartActive = sel && !["completed", "signed_off", "cancelled"].includes(sel.status);
  const downloadBuildSheet = useCallback(async (cart) => {
    try {
      const { data } = await api.get(`/carts/${cart.id}/build-sheet.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error("Couldn't generate the build sheet — please try again."); }
  }, []);
  const shippableLines = (sel?.lines || []).filter((l) => l.state === "reserved" || l.state === "picked");
  // Remove works at ANY stage before sign-off (draft/approved/in_pick/completed). Shipped lines
  // are Return, not remove. Bulk checkboxes only show when the SHIP selection column isn't in use.
  const canRemoveLines = !!sel && canBuild && sel.status !== "signed_off";
  const canAddParts = !!sel && canBuild && !["signed_off", "cancelled"].includes(sel.status);
  const shipChecksActive = canShip && cartActive && shippableLines.length > 0;
  const showRemoveChecks = canRemoveLines && !shipChecksActive;
  const removableLines = (sel?.lines || []).filter((l) => !l.shipment_number);
  const shippedLines = (sel?.lines || []).filter((l) => l.shipment_number);
  const showReturnChecks = canReturn && !!sel && sel.status === "completed" && shippedLines.length > 0;
  return (
    <div data-testid="pull-carts-page">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h1 className="font-display text-2xl font-bold tracking-tight mb-1 flex items-center gap-2">
          <ShoppingCart className="w-6 h-6 text-[#B01E28]" />Carts and Orders
        </h1>
        <div className="flex flex-wrap items-center gap-2 max-w-full">
          {awaiting != null && awaiting > 0 && (
            <Link to="/movement-history" data-testid="awaiting-truck-chip" title="Reserved + ship-ready parts still on the yard, waiting on the next run"
              className="inline-flex items-center gap-1.5 rounded-full border border-[#E0781A]/50 bg-orange-50 px-3 h-9 text-sm font-semibold text-[#B45309] hover:bg-orange-100 transition-colors">
              <Truck className="w-4 h-4" />{awaiting} awaiting next truck
            </Link>
          )}
          {(canPrice || canSignOff) && (
            <Button data-testid="pricing-queue-toggle" variant={showQueue ? "default" : "outline"}
              className={`rounded-lg h-9 ${showQueue ? "bg-[#B01E28] hover:bg-[#8f1820]" : "border-[#B01E28] text-[#B01E28] hover:bg-red-50"}`}
              onClick={() => { const nv = !showQueue; setShowQueue(nv); if (nv) loadQueue(); }}>
              <DollarSign className="w-4 h-4 mr-1" />Pricing Queue{queue?.cash_parts ? <span className="ml-1.5 inline-flex items-center justify-center min-w-5 h-5 px-1 rounded-full bg-white text-[#B01E28] text-[10px] font-bold">{queue.cash_parts}</span> : null}
            </Button>
          )}
          {(canBuild || canSignOff) && (
            <Button data-testid="completion-queue-toggle" variant={showCompletion ? "default" : "outline"}
              className={`rounded-lg h-9 ${showCompletion ? "bg-amber-600 hover:bg-amber-700" : "border-amber-600 text-amber-700 hover:bg-amber-50"}`}
              onClick={() => { const nv = !showCompletion; setShowCompletion(nv); if (nv) loadCompletion(); }}>
              <ClipboardList className="w-4 h-4 mr-1" />Needs Completion{completion?.count ? <span className="ml-1.5 inline-flex items-center justify-center min-w-5 h-5 px-1 rounded-full bg-white text-amber-700 text-[10px] font-bold">{completion.count}</span> : null}
            </Button>
          )}
          <SoundToggle />
        </div>
      </div>
      <p className="text-sm text-slate-500 mb-4">Build a cart of parts, assign it, and approve to reserve. This is an internal pull request — <b>never a payment</b>.</p>

      {showQueue && (
        <div className="border border-slate-200 bg-white p-4 mb-5" data-testid="pricing-queue-panel">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-base font-bold flex items-center gap-2"><DollarSign className="w-4 h-4 text-[#B01E28]" />Pricing Review Queue</h2>
            <button data-testid="pricing-queue-refresh" onClick={loadQueue} className="text-xs text-slate-400 hover:text-slate-700 underline">Refresh</button>
          </div>
          <p className="text-xs text-slate-500 mb-3">Parts left inventory without a price so the floor was never held up. Price them here — a sold part's cash then flows to its source donor's recovery. Foreman signs off once the sold parts are priced.</p>
          {queue?.week && (queue.week.cash_parts > 0 || queue.week.cash_recovered > 0 || queue.week.avoided_parts > 0 || queue.week.avoided_saved > 0) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
              <div data-testid="recovered-week-tile" className="flex items-center gap-3 rounded-lg border border-emerald-200 bg-gradient-to-r from-emerald-50 to-white p-3">
                <div className="w-10 h-10 rounded-full bg-emerald-600 text-white flex items-center justify-center shrink-0"><DollarSign className="w-5 h-5" /></div>
                <div>
                  <div className="font-display text-2xl font-black text-emerald-700 tabular-nums leading-none" data-testid="recovered-week-amount">${(queue.week.cash_recovered || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                  <div className="text-[11px] text-emerald-800 mt-1">cash recovered this week · <b>{queue.week.cash_parts || 0}</b> sold part{queue.week.cash_parts === 1 ? "" : "s"} priced → donor recovery</div>
                </div>
              </div>
              <div data-testid="avoided-week-tile" className={`flex items-center gap-3 rounded-lg border border-sky-200 bg-gradient-to-r from-sky-50 to-white p-3 ${queue.week.avoided_saved === undefined ? "hidden" : ""}`}>
                <div className="w-10 h-10 rounded-full bg-sky-600 text-white flex items-center justify-center shrink-0"><PiggyBank className="w-5 h-5" /></div>
                <div>
                  <div className="font-display text-2xl font-black text-sky-700 tabular-nums leading-none" data-testid="avoided-week-amount">${(queue.week.avoided_saved || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                  <div className="text-[11px] text-sky-800 mt-1">avoided cost this week · <b>{queue.week.avoided_parts || 0}</b> part{queue.week.avoided_parts === 1 ? "" : "s"} built internally → money saved</div>
                </div>
              </div>
            </div>
          )}
          <PricingQueue queue={queue} canPrice={canPrice} canSignOff={canSignOff} onPrice={priceRemoved} onSignOff={signOff} />
        </div>
      )}

      {showCompletion && (
        <div className="border border-slate-200 bg-white p-4 mb-5" data-testid="completion-queue-panel">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-base font-bold flex items-center gap-2"><ClipboardList className="w-4 h-4 text-amber-600" />Needs Completion — moved on tag + photo</h2>
            <button data-testid="completion-queue-refresh" onClick={loadCompletion} className="text-xs text-slate-400 hover:text-slate-700 underline">Refresh</button>
          </div>
          <p className="text-xs text-slate-500 mb-3">These parts moved (carted or transferred between sites) with only a tag + photo so the yard kept moving. Finish their identification from the photos — a cart can't be signed off until its parts are complete.</p>
          {!completion ? <div className="p-6 text-center"><Loader2 className="w-5 h-5 mx-auto animate-spin text-slate-300" /></div>
            : !(completion.parts || []).length ? <div className="p-10 text-center text-sm text-slate-400" data-testid="completion-queue-empty"><CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-emerald-300" />Nothing waiting — every moved part is fully identified.</div>
            : (
              <div className="border border-slate-200 rounded-lg divide-y divide-slate-100" data-testid="completion-queue">
                {completion.parts.map((g) => (
                  <div key={g.part_id} data-testid={`completion-part-${g.short_code}`} className="flex items-center gap-2 px-3 py-2 text-sm">
                    {g.photo ? <AuthImg fileId={g.photo} thumb className="w-9 h-9 rounded object-cover border border-slate-200" alt={g.short_code} />
                      : <div className="w-9 h-9 rounded bg-slate-100 flex items-center justify-center"><Package className="w-4 h-4 text-slate-400" /></div>}
                    <div className="min-w-0 flex-1">
                      <span className="font-mono font-bold">{g.short_code}</span> <span className="text-slate-500 text-xs">{g.name || "unnamed"}</span>
                      <span className={`ml-1.5 inline-flex items-center gap-1 text-[9px] font-bold uppercase rounded px-1.5 py-0.5 ${g.kind === "transfer" ? "bg-sky-50 text-sky-700 border border-sky-200" : "bg-amber-50 text-amber-700 border border-amber-200"}`}>{g.kind === "transfer" ? "transfer" : "cart"}{g.ctx ? ` · ${g.ctx}` : ""}</span>
                      <div className="text-[10px] text-amber-700 mt-0.5">needs: {(g.gaps || []).join(", ")}</div>
                    </div>
                    <Link to={`/sort-queue/${g.part_id}`} state={fromState({ pathname: "/pull-carts", search: sel ? `?open=${sel.id}` : "" }, sel ? assignmentShort(sel) : "the build/order")} data-testid={`completion-open-${g.short_code}`} className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-amber-700 hover:underline">Complete <ExternalLink className="w-3.5 h-3.5" /></Link>
                  </div>
                ))}
              </div>
            )}
        </div>
      )}

      {/* ONE PO = ONE JOB — if two open jobs ended up on the same PO / truck / order,
          say so here and offer to put them together. Nothing merges without a confirm. */}
      {canBuild && dupes?.groups?.length > 0 && (
        <div data-testid="merge-strip" className="mb-5 border-2 border-[#E0781A] bg-orange-50 p-3">
          <div className="flex items-start gap-2">
            <Merge className="w-5 h-5 text-[#B45309] shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <div className="font-display text-base font-bold text-[#B45309]" data-testid="merge-strip-title">
                {dupes.groups.length} {dupes.groups.length === 1 ? "job is" : "jobs are"} doubled up — same PO, truck or order on more than one open job
              </div>
              <div className="mt-2 space-y-1.5">
                {dupes.groups.map((g) => { const gk = `${g.kind}-${String(g.key).replace(/[^A-Za-z0-9]+/g, "-")}`; return (
                  <div key={gk} data-testid={`merge-group-${gk}`} className="flex flex-wrap items-center gap-2 rounded-lg bg-white border border-orange-200 px-2.5 py-2">
                    <span className="text-xs font-semibold text-slate-700">{g.label}</span>
                    <span className="text-xs text-slate-500">
                      {g.jobs.map((c) => `${c.cart_number} (${c.line_count})`).join(" + ")}
                    </span>
                    <button data-testid={`merge-group-open-${gk}`}
                      onClick={() => setMergeAsk({ key: gk, label: g.label, into: g.jobs[0], from: g.jobs.slice(1) })}
                      className="ml-auto shrink-0 inline-flex items-center gap-1 rounded-lg bg-[#E0781A] hover:bg-[#C76714] text-white text-xs font-bold px-2.5 h-8">
                      <Merge className="w-3.5 h-3.5" />Put them together
                    </button>
                  </div>
                ); })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PHOTO OWED — parts that were let out on the alert with a logged reason. The job
          still can't be signed off until each one has its tag and photo. */}
      {canBuild && owed?.count > 0 && (
        <div data-testid="photo-owed-strip" className="mb-5 border-2 border-[#B01E28] bg-red-50 p-3">
          <div className="flex items-start gap-2">
            <Camera className="w-5 h-5 text-[#B01E28] shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <div className="font-display text-base font-bold text-[#B01E28]" data-testid="photo-owed-title">
                {owed.count} part{owed.count === 1 ? "" : "s"} owe{owed.count === 1 ? "s" : ""} a tag or photo — they went out on an alert
              </div>
              <p className="text-xs text-red-800/80 mt-0.5">Each one was let go with a logged reason. The job it is on can't be signed off until this is finished.</p>
              <button data-testid="photo-owed-toggle" onClick={() => setOwedOpen((v) => !v)}
                className="mt-1.5 text-xs font-bold text-[#B01E28] underline hover:text-[#8f1820]">
                {owedOpen ? "Hide the list" : "Show me which parts"}
              </button>
              {owedOpen && (
                <div data-testid="photo-owed-list" className="mt-2 border border-red-200 rounded-lg bg-white divide-y divide-red-100">
                  {owed.parts.filter((pt) => !pt.settled).map((pt) => (
                    <div key={pt.part_id} data-testid={`photo-owed-${pt.short_code}`} className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm">
                      <span className="font-mono font-bold">{pt.short_code}</span>
                      <span className="text-slate-500 text-xs truncate max-w-[16rem]">{pt.name || "unnamed"}</span>
                      <span className="text-[10px] font-bold uppercase rounded px-1.5 py-0.5 bg-red-50 text-[#B01E28] border border-red-200">owes {(pt.still_missing || []).join(" + ")}</span>
                      {pt.cart_number && <span className="text-[11px] text-slate-500">on {pt.cart_number}{pt.job_name ? ` · ${pt.job_name}` : ""}</span>}
                      <Link to={`/sort-queue/${pt.part_id}`} state={fromState({ pathname: "/pull-carts" }, "the job list")}
                        data-testid={`photo-owed-open-${pt.short_code}`}
                        className="ml-auto shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-[#B01E28] hover:underline">Finish it <ExternalLink className="w-3.5 h-3.5" /></Link>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}


      <div className="grid grid-cols-1 lg:grid-cols-[330px_1fr] gap-5">
        {/* Cart list */}
        <div className="border border-slate-200 bg-white">
          <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200">
            <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Carts</span>
            {canBuild && <Button data-testid="new-cart-button" size="sm" className="rounded-lg h-7 bg-[#E0781A] hover:bg-[#C76714]" onClick={() => setCreating((v) => !v)}><Plus className="w-3.5 h-3.5 mr-1" />New</Button>}
          </div>
          {creating && (
            <div className="p-3 border-b border-slate-200 bg-slate-50 space-y-2" data-testid="new-cart-form">
              <div className="grid grid-cols-3 gap-1">
                {[["build", "Build", Wrench], ["customer_order", "Customer", Building2], ["internal_use", "Internal", Package]].map(([v, lbl, Icon]) => (
                  <button key={v} data-testid={`atype-${v}`} onClick={() => setAtype(v)} className={`text-xs py-3 border flex flex-col items-center gap-1 ${atype === v ? "border-[#B01E28] bg-white text-[#B01E28]" : "border-slate-200 text-slate-500"}`}>
                    <Icon className="w-4 h-4" />{lbl}
                  </button>
                ))}
              </div>
              {/* EVERY JOB IS NAMED — his rule. Blank still works: the job names itself from the PO,
                  the customer or the purpose, so no job is ever just a number. */}
              <Input data-testid="job-name" placeholder={atype === "build" ? "Name this job — e.g. Unit 4411 cab rebuild" : atype === "customer_order" ? "Name this job (optional — the customer is used)" : "Name this job (optional — the purpose is used)"}
                value={jobName} onChange={(e) => setJobName(e.target.value)} className="rounded-lg h-11 text-sm font-semibold" />
              {atype === "build" && (
                <>
                  {/* PICK THE TRUCK — a dropdown you can type into. The PO follows the truck. */}
                  <div className="relative">
                    <Input data-testid="build-builder-search" value={builder ? `${builder.stock_number || ""} · ${builder.vehicle || [builder.year, builder.make, builder.model].filter(Boolean).join(" ")}` : builderQ}
                      onChange={(e) => { setBuilderQ(e.target.value); if (builderId) selectBuilder(null); }}
                      onKeyDown={(e) => { if (e.key === "Enter" && !builderId) { e.preventDefault(); builderEnter(e.currentTarget.value); } }}
                      onFocus={() => setBuilderOpen(true)} enterKeyHint="search"
                      placeholder="Which truck is this for? — type a stock #, year, make or model"
                      className="rounded-lg h-11 text-sm pr-8" />
                    {builderId
                      ? <button data-testid="build-builder-clear" onClick={() => { selectBuilder(null); setBuilderQ(""); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
                      : <ChevronDown className="w-4 h-4 text-slate-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />}
                    {builderOpen && !builderId && (
                      <div data-testid="build-builder-list" className="absolute z-20 left-0 right-0 mt-1 max-h-56 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg">
                        {builderHits.length === 0
                          ? <div className="px-3 py-2 text-xs text-slate-400">{builderRes.loading ? "Searching…" : "No builder truck matches — type a PO below instead."}</div>
                          : builderHits.map((t) => (
                            <button key={t.id} data-testid={`build-builder-opt-${t.stock_number}`} onClick={() => { selectBuilder(t); setBuilderOpen(false); }}
                              className="w-full text-left px-3 py-2 hover:bg-amber-50 border-b border-slate-100 last:border-0">
                              <div className="font-mono font-bold text-sm text-[#E0781A]">{t.stock_number}</div>
                              <div className="text-xs text-slate-600">{[t.year, t.make, t.model].filter(Boolean).join(" ")}{t.po_number ? ` · PO ${t.po_number}` : " · no PO on file"}</div>
                            </button>))}
                        {builderRes.total > builderHits.length && (
                          <div data-testid="build-builder-more" className="px-3 py-2 text-[11px] font-semibold text-[#C76714] bg-[#FFF9F3]">
                            Showing {builderHits.length} of {builderRes.total} builder trucks — type more to narrow it down.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <Input data-testid="build-po" placeholder="PO number (required)" value={po}
                    onChange={(e) => setPo(e.target.value)} readOnly={!!builderId}
                    className={`rounded-lg h-11 text-sm font-mono ${builderId ? "bg-slate-100 text-slate-500 cursor-not-allowed" : ""}`} />
                  {builderId && <p data-testid="build-po-autofill" className="text-[11px] text-indigo-600">PO auto-filled from the selected builder — it can't be retyped, so it can never mismatch.</p>}
                  {!builderId && poMatch && (
                    <div data-testid="build-po-match" className="flex items-center justify-between gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-2.5 py-1.5 text-[11px] text-indigo-700">
                      <span>PO <b className="font-mono">{poMatch.po_number}</b> belongs to builder <b>{poMatch.stock_number}</b>{poMatch.vehicle ? ` · ${poMatch.vehicle}` : ""}.</span>
                      <button data-testid="build-po-match-link" onClick={() => selectBuilder(poMatch)} className="shrink-0 underline font-semibold hover:text-indigo-900">Link this builder</button>
                    </div>
                  )}
                </>
              )}
              {atype === "customer_order" && (
                <>
                  <Input data-testid="co-customer-name" placeholder="Customer name (required)" value={custName} onChange={(e) => setCustName(e.target.value)} className="rounded-lg h-11 text-sm" />
                  <label className="flex items-center gap-2 text-[11px] text-slate-600 py-0.5"><input data-testid="co-walkin" type="checkbox" checked={walkIn} onChange={(e) => setWalkIn(e.target.checked)} className="accent-[#B01E28]" />Walk-in / counter sale (auto-receipt)</label>
                  <Input data-testid="co-contact" placeholder="Contact — phone/email (optional)" value={contact} onChange={(e) => setContact(e.target.value)} className="rounded-lg h-11 text-sm" />
                  <Input data-testid="assign-notes" placeholder="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} className="rounded-lg h-11 text-sm" />
                  {/* HIS RULE 2026-10-17: the customer's order / invoice # sits AT THE BOTTOM and is
                      OPTIONAL. If it is left blank the counter reference is stamped automatically,
                      so the sale is still traceable and nobody is held up at the counter. */}
                  {!walkIn && (
                    <div className="pt-1 border-t border-slate-200">
                      <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400 pt-1 pb-1">Their paperwork — optional</div>
                      <Input data-testid="co-order-ref" placeholder="Order / invoice # (optional)" value={orderRef} onChange={(e) => setOrderRef(e.target.value)} className="rounded-lg h-11 text-sm" />
                      <Input data-testid="co-po" placeholder="Customer PO # (optional)" value={custPo} onChange={(e) => setCustPo(e.target.value)} className="rounded-lg h-11 text-sm font-mono mt-2" />
                    </div>
                  )}
                  {walkIn && <p data-testid="co-walkin-note" className="text-[11px] text-emerald-600">A counter-receipt reference will be auto-stamped — the sale stays traceable without blocking it.</p>}
                </>
              )}
              {atype === "internal_use" && (
                <Input data-testid="assign-notes" placeholder="Purpose / reason (required)" value={notes} onChange={(e) => setNotes(e.target.value)} className="rounded-lg h-11 text-sm" />
              )}
              <Button data-testid="create-cart-confirm" className="w-full rounded-lg h-11" onClick={() => createCart(false)}>Create draft</Button>
            </div>
          )}
          <div className="px-2 pt-2 pb-1 border-b border-slate-100">
            <div className="relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <Input data-testid="cart-search" value={cartQ} onChange={(e) => setCartQ(e.target.value)}
                placeholder="Find a cart — PO, build, customer, cart #" className="rounded-lg h-9 text-sm pl-8 pr-8" />
              {cartQ && <button data-testid="cart-search-clear" onClick={() => setCartQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>}
            </div>
          </div>
          <div className="max-h-[64dvh] overflow-y-auto" data-testid="cart-list">
            {(() => {
              const searching = cartQ.trim().length > 0;
              // When searching, use SERVER-SIDE results (every cart, any status/age) — never a
              // client filter over loaded rows (that would hide older carts = looks like data loss).
              const shown = searching ? searchResults : carts;
              if (searching && searchLoading && shown === null)
                return <div className="p-4 text-center text-xs text-slate-400" data-testid="cart-search-loading">Searching all carts…</div>;
              if (!searching && carts.length === 0) return <div className="p-4 text-center text-xs text-slate-400">No carts yet</div>;
              if ((shown || []).length === 0) return <div className="p-4 text-center text-xs text-slate-400" data-testid="cart-search-empty">No carts match "{cartQ}"</div>;
              return [["draft", "Drafts (in progress)"], ["approved", "Awaiting Pick"], ["in_pick", "In Pick"], ["completed", "Completed"], ["signed_off", "Signed off"], ["cancelled", "Cancelled"]].map(([st, label]) => {
                const group = (shown || []).filter((c) => c.status === st);
                if (!group.length) return null;
                return (
                  <div key={st} data-testid={`cart-group-${st}`}>
                    <div className="px-3 py-1.5 bg-slate-50 border-b border-slate-200 font-mono text-[10px] uppercase tracking-widest text-slate-500 flex items-center justify-between sticky top-0">
                      <span>{label}</span><span>{group.length}</span>
                    </div>
                    {group.map((c) => (
                      <button key={c.id} data-testid={`cart-row-${c.cart_number}`} onClick={() => openCart(c.id)} className={`w-full text-left px-3 py-2.5 border-b border-slate-100 hover:bg-slate-50 ${sel?.id === c.id ? "bg-slate-50" : ""}`}>
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-semibold text-sm text-[#1F1F1F] truncate" data-testid={`cart-row-name-${c.cart_number}`}>{c.name || assignmentShort(c)}</span>
                          <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded shrink-0 ${STATUS_PILL[c.status]}`}>{c.status.replace("_", " ")}</span>
                        </div>
                        {c.name && <div className="text-[11px] text-slate-400 truncate">{assignmentShort(c)}</div>}
                        <div className="text-[11px] text-slate-500 mt-0.5"><span className="font-mono font-bold text-slate-400">{c.cart_number}</span> · {c.line_count} parts · {money(c.parts_total)}</div>
                        <div className="text-[10px] text-slate-400 mt-0.5"><UserName email={c.created_by} plain /> · {(c.created_at || "").slice(0, 10)}</div>
                      </button>
                    ))}
                  </div>
                );
              }); })()}
            {termHasMore && !cartQ.trim() && (() => {
              const loadedTerm = carts.filter((c) => ["completed", "signed_off", "cancelled"].includes(c.status)).length;
              const more = Math.max(termTotal - loadedTerm, 0);
              return (
                <button data-testid="cart-load-more" onClick={loadMoreTerminal}
                  className="w-full px-3 py-2.5 text-xs font-semibold text-[#B01E28] hover:bg-red-50 border-t border-slate-200">
                  Load more completed / signed-off carts{more ? ` (${more} more)` : ""}
                </button>
              );
            })()}
            {cartQ.trim() && searchHasMore && (
              <div data-testid="cart-search-more-note" className="px-3 py-2 text-[10px] text-slate-400 border-t border-slate-100">
                Showing the first {(searchResults || []).length} matches — type more of the PO / cart # to narrow it down.
              </div>
            )}
          </div>
        </div>

        {/* Builder / detail */}
        <div className="border border-slate-200 bg-white p-4">
          {!sel ? (
            <div className="text-center text-sm text-slate-400 py-16"><ClipboardList className="w-10 h-10 mx-auto mb-2 text-slate-200" />Select or create a cart.</div>
          ) : (
            <div data-testid="cart-detail">
              <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
                <div className="min-w-0">
                  {sel.name && (
                    <div className="flex items-center gap-2 mb-0.5">
                      <h1 data-testid="cart-name" className="font-display text-2xl font-black tracking-tight leading-tight text-[#B01E28] break-words">{sel.name}</h1>
                      {canBuild && sel.status !== "signed_off" && sel.status !== "cancelled" && (
                        <button data-testid="cart-rename" title="Rename this job" onClick={() => { setRenameTo(sel.name || ""); setRenaming(true); }}
                          className="shrink-0 text-xs text-slate-400 hover:text-[#B01E28] underline">rename</button>
                      )}
                    </div>
                  )}
                  <h2 className={`font-display tracking-tight leading-tight text-[#1F1F1F] ${sel.name ? "text-base font-bold text-slate-600" : "text-2xl font-black"}`} data-testid="cart-assignment">
                    {sel.assignment_type === "build" && <span className="inline-flex items-center gap-2"><Wrench className="w-5 h-5 text-slate-400 shrink-0" />PO {sel.build?.po_number || "—"}{sel.build?.name ? <span className="text-slate-500 font-bold"> · {sel.build.name}</span> : null}</span>}
                    {sel.assignment_type === "customer_order" && (() => { const co = sel.customer_order || {}; const ref = co.order_ref || co.po_number; return (
                      <span className="inline-flex items-center gap-2 flex-wrap"><Building2 className="w-5 h-5 text-slate-400 shrink-0" />{co.customer_name || "Customer Order"}{co.walk_in ? <span className="text-slate-500 font-bold"> · walk-in</span> : ""}{ref ? <span className="font-mono text-slate-500 font-bold"> · {ref}</span> : ""}</span>
                    ); })()}
                    {sel.assignment_type === "internal_use" && <span className="inline-flex items-center gap-2"><Package className="w-5 h-5 text-slate-400 shrink-0" />Internal Use{(sel.internal_use?.purpose || sel.internal_use?.notes) ? <span className="text-slate-500 font-bold"> · {sel.internal_use.purpose || sel.internal_use.notes}</span> : ""}</span>}
                  </h2>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="font-mono text-xs font-bold text-slate-400">{sel.cart_number}</span>
                    <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded ${STATUS_PILL[sel.status]}`}>{sel.status.replace("_", " ")}</span>
                    {sel.assignment_type === "customer_order" && (() => { const co = sel.customer_order || {}; return co.contact ? <span className="text-[11px] text-slate-400">· {co.contact}</span> : null; })()}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 max-w-full">
                  {canApprove && isDraft && <Button data-testid="approve-cart-button" className="rounded-lg h-11 px-4 text-base bg-emerald-600 hover:bg-emerald-700" onClick={approve}><CheckCircle2 className="w-5 h-5 mr-1.5" />Approve</Button>}
                  {canBuild && isDraft && <Button data-testid="discard-draft-button" variant="outline" className="rounded-lg h-11 px-4 text-base border-slate-300 text-slate-500 hover:bg-slate-50" onClick={() => setDiscardConfirm(true)}><Trash2 className="w-5 h-5 mr-1.5" />Discard draft</Button>}
                  {(sel.status === "approved" || sel.status === "in_pick") && <Link to={`/pick/${sel.id}`} data-testid="go-to-picking" className="inline-flex items-center rounded-lg border border-slate-300 bg-white text-slate-700 px-4 h-11 text-base hover:bg-slate-50"><Truck className="w-5 h-5 mr-1.5" />Pull-station view</Link>}
                  {canApprove && (sel.status === "approved" || sel.status === "in_pick") && <Button data-testid="cancel-cart-button" variant="outline" className="rounded-lg h-11 px-4 text-base border-[#B01E28] text-[#B01E28] hover:bg-red-50" title="Cancels the WHOLE order and releases every part — not the same as removing individual lines" onClick={() => setVoidConfirm(true)}><Trash2 className="w-5 h-5 mr-1.5" />Void entire order</Button>}
                  {canSignOff && sel.status === "completed" && <Button data-testid="sign-off-button" className="rounded-lg h-11 px-4 text-base bg-emerald-600 hover:bg-emerald-700" onClick={() => setSignoffConfirm(true)}><ShieldCheck className="w-5 h-5 mr-1.5" />Sign off</Button>}
                  {sel.lines?.length > 0 && (
                    <Button data-testid={sel.assignment_type === "build" ? "build-ticket-button" : "parts-ticket-button"}
                      variant="outline" className="rounded-lg h-11 px-4 text-base border-slate-300 text-slate-700 hover:bg-slate-50"
                      title="Opens the ticket ready to print — nothing prints on its own"
                      onClick={() => openTicket(sel, sel.assignment_type === "build" ? "build-ticket" : "parts-ticket")}>
                      <Receipt className="w-5 h-5 mr-1.5" />{sel.assignment_type === "build" ? "Build Ticket" : "Parts Ticket"}
                    </Button>
                  )}
                  {sel.lines?.length > 0 && <Button data-testid="build-sheet-pdf" variant="outline" className="rounded-lg h-11 px-3 text-sm border-slate-200 text-slate-500 hover:bg-slate-50" title="The older pull sheet, as a PDF" onClick={() => downloadBuildSheet(sel)}><FileText className="w-4 h-4 mr-1" />Pull sheet PDF</Button>}
                </div>
              </div>

              <CartTrail cart={sel} shipments={shipments} />

              {canPick && (sel.status === "approved" || sel.status === "in_pick") && (
                <InlinePickPanel cart={sel} onPicked={() => loadCart(sel.id)} />
              )}

              {isDraft && (() => {
                const contributors = [...new Set((sel.lines || []).map((l) => l.added_by).filter(Boolean))];
                const others = contributors.filter((e) => e !== sel.created_by);
                return (
                  <div data-testid="cart-building-by" className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-slate-500 -mt-1 mb-3">
                    <span className="inline-flex items-center gap-1 text-slate-600"><Users className="w-3.5 h-3.5 text-[#E0781A]" />Building:</span>
                    <span data-testid="building-started-by" className="font-semibold text-slate-700"><UserName email={sel.created_by} /></span>
                    <span className="text-slate-400">(started)</span>
                    {others.map((e) => (
                      <span key={e} data-testid={`building-contributor-${e}`} className="inline-flex items-center rounded-full bg-slate-100 px-1.5 py-0.5 font-medium text-slate-600"><UserName email={e} /></span>
                    ))}
                    {sel.updated_by && sel.updated_by !== sel.created_by && (
                      <span data-testid="building-last-by" className="text-slate-400">· last edit by <b className="font-semibold text-slate-600"><UserName email={sel.updated_by} /></b></span>
                    )}
                  </div>
                );
              })()}

              {canAddParts && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                  <div className="border border-slate-200 p-3">
                    <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1 mb-2"><ScanLine className="w-3.5 h-3.5 text-[#B01E28]" />Scan to add{!isDraft && <span className="text-slate-400 normal-case font-normal ml-1">· adds to this {sel.status.replace("_", " ")} order</span>}</Label>
                    <ScanField testid="cart-scan" submitLabel="Add" continuous paused={!!(noPhoto || fieldAdd || otherWarn)}
                      placeholder="Scan FAS tag / part code" onScan={addByScan} />
                  </div>
                  <form onSubmit={doSearch} className="border border-slate-200 p-3">
                    <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1"><Search className="w-3.5 h-3.5 text-[#B01E28]" />Search to add</Label>
                    <div className="flex gap-1 mt-2">
                      <Input data-testid="cart-search-input" placeholder="Name, brand, OEM…" value={q} onChange={(e) => setQ(e.target.value)} className="rounded-lg h-10 text-sm" />
                      <Button type="submit" variant="outline" className="rounded-lg h-10" data-testid="cart-search-go">Go</Button>
                    </div>
                  </form>
                </div>
              )}

              {canAddParts && lastAdded && (
                <div data-testid="scan-added-flash" className="flex items-center gap-3 mb-4 rounded-lg border-2 border-emerald-400 bg-emerald-50 p-2.5 animate-in fade-in">
                  <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0" />
                  {lastAdded.photo
                    ? <AuthImg fileId={lastAdded.photo} thumb className="w-12 h-12 rounded object-cover border border-emerald-200" alt={lastAdded.short_code} />
                    : <div className="w-12 h-12 rounded bg-emerald-100 flex items-center justify-center"><Package className="w-5 h-5 text-emerald-500" /></div>}
                  <div className="min-w-0">
                    <div className="font-bold text-emerald-900 text-sm">Added · {lastAdded.short_code}</div>
                    <div className="text-xs text-emerald-800 truncate">{lastAdded.name}</div>
                    <div className="text-[11px] font-mono text-emerald-700">{lastAdded.location_label || "no bin"}</div>
                  </div>
                  <span className="ml-auto text-[10px] font-mono text-emerald-600 uppercase">scan next →</span>
                </div>
              )}

              {results.length > 0 && canAddParts && (
                <div className="border border-slate-200 mb-4 max-h-44 overflow-y-auto" data-testid="search-results">
                  {results.map((p) => (
                    <div key={p.id} className="flex items-center justify-between px-3 py-2 border-b border-slate-100 text-sm">
                      <span className="font-mono">{p.short_code} <span className="text-slate-500">{p.name} · {p.brand}</span></span>
                      <Button size="sm" variant="outline" className="rounded-lg h-7" onClick={() => addById(p.id)} data-testid={`add-result-${p.short_code}`}>Add</Button>
                    </div>
                  ))}
                </div>
              )}

              {/* Send-selected bar (partial shipment) */}
              {canShip && cartActive && shippableLines.length > 0 && (
                <div className="flex items-center justify-between mb-2 px-3 py-2 rounded-lg bg-slate-50 border border-slate-200" data-testid="ship-bar">
                  <span className="text-xs text-slate-500">{shipSel.length ? `${shipSel.length} selected to send` : "Select parts to send now — the rest of the cart stays open."}</span>
                  <div className="flex items-center gap-2">
                    <button data-testid="ship-select-all" onClick={() => setShipSel(shipSel.length === shippableLines.length ? [] : shippableLines.map((l) => l.part_id))} className="min-h-[44px] px-3 text-sm font-semibold text-slate-600 hover:text-slate-900 underline transition-colors">{shipSel.length === shippableLines.length ? "Clear" : "Select all"}</button>
                    <Button size="sm" data-testid="open-ship-sheet" disabled={!shipSel.length} className="rounded-lg bg-emerald-600 hover:bg-emerald-700 h-8 font-bold" onClick={() => setShipSheet(true)}><Truck className="w-4 h-4 mr-1" />Send selected ({shipSel.length})</Button>
                  </div>
                </div>
              )}

              {/* Return-selected bar (post-ship: reverse sale + send to put-away) */}
              {showReturnChecks && (
                <div className="flex items-center justify-between mb-2 px-3 py-2 rounded-lg bg-sky-50 border border-sky-200" data-testid="return-bar">
                  <span className="text-xs text-slate-600">{returnSel.length ? `${returnSel.length} shipped part(s) selected to return` : "Select shipped parts to return to stock (goes to put-away)."}</span>
                  <div className="flex items-center gap-2">
                    <button data-testid="return-select-all" onClick={() => setReturnSel(returnSel.length === shippedLines.length ? [] : shippedLines.map((l) => l.part_id))} className="text-xs text-slate-500 hover:text-slate-800 underline">{returnSel.length === shippedLines.length ? "Clear" : "Select all"}</button>
                    <Button size="sm" data-testid="bulk-return-btn" disabled={!returnSel.length} className="rounded-lg bg-sky-600 hover:bg-sky-700 h-8 font-bold" onClick={() => doReturn(returnSel)}><Undo2 className="w-4 h-4 mr-1" />Return to stock ({returnSel.length})</Button>
                  </div>
                </div>
              )}

              {/* Remove-selected bar (pre-sign-off: un-reserve/un-pick parts, no ledger) */}
              {showRemoveChecks && removableLines.length > 0 && (
                <div className="flex items-center justify-between mb-2 px-3 py-2 rounded-lg bg-red-50 border border-red-200" data-testid="remove-bar">
                  <span className="text-xs text-slate-600">{removeSel.length ? `${removeSel.length} selected to remove` : "Select parts to remove from this build/order."}</span>
                  <div className="flex items-center gap-2">
                    <button data-testid="remove-select-all" onClick={() => setRemoveSel(removeSel.length === removableLines.length ? [] : removableLines.map((l) => l.part_id))} className="text-xs text-slate-500 hover:text-slate-800 underline">{removeSel.length === removableLines.length ? "Clear" : "Select all"}</button>
                    <Button size="sm" data-testid="bulk-remove-btn" disabled={!removeSel.length} className="rounded-lg bg-[#B01E28] hover:bg-[#8f1820] h-8 font-bold" onClick={bulkRemove}><Trash2 className="w-4 h-4 mr-1" />Remove from build/order ({removeSel.length})</Button>
                  </div>
                </div>
              )}

              {/* Lines */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="cart-lines">
                  <thead><tr className="text-left font-mono text-[10px] uppercase text-slate-400 border-b border-slate-200">
                    {(shipChecksActive || showRemoveChecks || showReturnChecks) ? <th className="py-2 pr-2 w-6"></th> : null}<th className="py-2 pr-2">Part</th><th className="pr-2">Bin</th><th className="pr-2">Retail</th><th className="pr-2">State</th><th></th>
                  </tr></thead>
                  <tbody>
                    {sel.lines.length === 0 ? <tr><td colSpan={6} className="py-6 text-center text-slate-400 text-xs">No parts yet — scan or search to add.</td></tr> :
                      sel.lines.map((l) => {
                        const selectable = canShip && cartActive && (l.state === "reserved" || l.state === "picked");
                        return (
                        <tr key={l.part_id} data-testid={`line-${l.short_code}`} className={`border-b border-slate-100 ${shipSel.includes(l.part_id) ? "bg-emerald-50" : ""}`}>
                          {(shipChecksActive || showRemoveChecks || showReturnChecks) && <td className="py-2 pr-2">{showReturnChecks ? (l.shipment_number && !l.returned && <input type="checkbox" data-testid={`return-check-${l.short_code}`} checked={returnSel.includes(l.part_id)} onChange={() => toggleReturn(l.part_id)} className="w-4 h-4 accent-sky-600" />) : showRemoveChecks ? (!l.shipment_number && <input type="checkbox" data-testid={`remove-check-${l.short_code}`} checked={removeSel.includes(l.part_id)} onChange={() => toggleRemove(l.part_id)} className="w-4 h-4 accent-[#B01E28]" />) : (selectable && <label className="inline-flex items-center justify-center w-11 h-11 -m-2 cursor-pointer"><input type="checkbox" data-testid={`ship-check-${l.short_code}`} checked={shipSel.includes(l.part_id)} onChange={() => toggleShip(l.part_id)} className="w-6 h-6 accent-emerald-600" /></label>)}</td>}
                          <td className="py-2 pr-2"><div className="flex items-start gap-2"><LinePhoto fileId={l.photo} alt={l.short_code} testid={`line-photo-${l.short_code}`} /><div className="min-w-0"><Link to={`/parts/${l.part_id}`} state={fromState({ pathname: "/pull-carts", search: `?open=${sel.id}` }, assignmentShort(sel))} data-testid={`line-open-${l.short_code}`} className="font-mono font-bold text-[#E0781A] hover:underline">{l.short_code}</Link> <span className="text-slate-700 text-xs font-medium">{l.name || <span className="italic text-slate-400">unnamed — tap to view</span>}</span>{l.field_added && <span data-testid={`line-field-added-${l.short_code}`} className="ml-1.5 inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5"><ClipboardList className="w-2.5 h-2.5" />field-added</span>}{l.shipment_number && <span className="ml-1.5 inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5"><Truck className="w-2.5 h-2.5" />{l.shipment_number}</span>}{l.left_without_tag_or_photo && <span data-testid={`line-no-tag-photo-${l.short_code}`} title={`Left without ${(l.left_without_tag_or_photo.missing || []).join(" + ")} · ${l.left_without_tag_or_photo.by || ""} · ${l.left_without_tag_or_photo.reason || ""} — needed before sign-off`} className="ml-1.5 inline-flex items-center gap-1 text-[10px] font-bold text-white bg-[#B01E28] rounded px-1.5 py-0.5"><AlertTriangle className="w-2.5 h-2.5" />no tag/photo</span>}{l.added_without_tag_or_photo && !l.left_without_tag_or_photo && <span data-testid={`line-added-no-photo-${l.short_code}`} title={`Went on the job with no ${gapWords(l.added_without_tag_or_photo.missing)} · ${l.added_without_tag_or_photo.by || ""} · ${l.added_without_tag_or_photo.reason || ""}${l.added_without_tag_or_photo.note ? ` — ${l.added_without_tag_or_photo.note}` : ""} — needed before sign-off`} className="ml-1.5 inline-flex items-center gap-1 text-[10px] font-bold text-amber-900 bg-amber-100 border border-amber-400 rounded px-1.5 py-0.5"><AlertTriangle className="w-2.5 h-2.5" />no {gapWords(l.added_without_tag_or_photo.missing)} — logged</span>}{l.picked_without_bin && <span data-testid={`line-no-bin-${l.short_code}`} title={`Picked without a bin scan${l.bin_override_by ? ` · ${l.bin_override_by}` : ""}${l.bin_override_reason ? ` · ${l.bin_override_reason}` : ""}`} className="ml-1.5 inline-flex items-center gap-1 text-[10px] font-semibold text-amber-800 bg-amber-100 border border-amber-300 rounded px-1.5 py-0.5"><AlertTriangle className="w-2.5 h-2.5" />no bin</span>}{l.state === "short" && l.short_note && <div className="text-[10px] text-red-600">⚠ {l.short_note}</div>}{l.conflict && <div data-testid={`line-conflict-${l.short_code}`} className="inline-flex items-center gap-1 mt-0.5 text-[10px] font-semibold text-white bg-[#B01E28] rounded px-1.5 py-0.5"><AlertTriangle className="w-3 h-3" />{l.conflict.message}</div>}</div></div></td>
                          <td className="pr-2 text-xs text-slate-500"><WhereItIs location={l.location} size="sm" testid={`cart-line-where-${l.short_code}`} fallback="—" /></td>
                          <td className="pr-2">
                            <div className="flex items-center gap-1.5">
                              {canPrice && sel.status !== "signed_off" ? (
                                <input data-testid={`price-${l.short_code}`} type="number" defaultValue={l.retail_price ?? ""} onBlur={(e) => e.target.value && setPrice(l.part_id, e.target.value)} className="w-20 border border-slate-200 rounded-lg px-1 py-0.5 text-sm font-mono" placeholder="$" />
                              ) : money(l.retail_price)}
                              {/* THE FOREMAN'S TICK — ✓ means he entered or checked this used retail
                                  price; ? means it is still an estimate and the ticket says so. */}
                              {l.price_checked_at
                                ? <span data-testid={`price-checked-${l.short_code}`} title={`Checked by ${l.price_checked_by || ""}`} className="text-emerald-600 font-black">✓</span>
                                : l.retail_price > 0
                                  ? (canPrice && sel.status !== "signed_off"
                                      ? <button data-testid={`price-check-${l.short_code}`} title="Tick this price as checked by the foreman" onClick={() => checkPrice(l.part_id)} className="text-[#E0781A] font-black hover:text-[#C76714]">?</button>
                                      : <span data-testid={`price-unchecked-${l.short_code}`} className="text-[#E0781A] font-black">?</span>)
                                  : <span data-testid={`price-none-${l.short_code}`} title="No price on this part yet" className="text-slate-400 text-[11px] font-semibold">no price yet</span>}
                            </div>
                          </td>
                          <td className="pr-2">{(() => { const sv = lineStateView(l); return <span data-testid={`line-state-${l.short_code}`} className={`font-mono text-[10px] uppercase ${LINE_PILL[sv.key] || "text-slate-500"}`}>{sv.label}</span>; })()}</td>
                          <td className="text-right whitespace-nowrap">
                            {canRemoveLines && !l.shipment_number && <button data-testid={`remove-line-${l.short_code}`} title="Remove from build/order" onClick={() => removeLine(l.part_id)} className="text-slate-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>}
                            {l.returned ? <span data-testid={`returned-badge-${l.short_code}`} className="inline-flex items-center gap-1 text-xs font-bold text-sky-600"><Undo2 className="w-4 h-4" />Returned</span>
                              : (canReturn && sel.status === "completed" && l.shipment_number && <button data-testid={`return-line-${l.short_code}`} title="Return this shipped part to stock (goes to put-away)" onClick={() => doReturn(l.part_id)} className="inline-flex items-center gap-1 text-xs font-bold text-sky-700 hover:text-sky-900"><Undo2 className="w-4 h-4" />Return</button>)}
                            {canPick && cartActive && l.state === "reserved" && (
                              <span className="inline-flex items-center gap-1.5">
                                {l.field_added && <button data-testid={`inline-line-pick-${l.short_code}`} onClick={() => pickLine(l)} className="inline-flex items-center gap-1 text-[11px] text-white bg-[#E0781A] hover:bg-[#C76714] px-2 py-1 rounded"><PackageCheck className="w-3.5 h-3.5" />Pick</button>}
                                <button data-testid={`inline-cant-find-${l.short_code}`} onClick={() => shortPick(l)} className="inline-flex items-center gap-1 text-[11px] text-red-600 border border-red-200 px-2 py-1 rounded hover:bg-red-50"><AlertTriangle className="w-3.5 h-3.5" />Can't find</button>
                              </span>
                            )}
                          </td>
                        </tr>
                      ); })}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-200">
                <span className="text-xs text-slate-400 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Parts retail total only — no job/cost total.</span>
                <span className="font-mono text-lg font-black" data-testid="cart-total">{money(sel.parts_total)}</span>
              </div>

              {/* Shipment history — what went when, under this PO */}
              {shipments.length > 0 && (
                <div className="mt-4 pt-3 border-t border-slate-200" data-testid="cart-shipments">
                  <h3 className="text-xs font-bold uppercase text-slate-400 mb-2 flex items-center gap-1"><Truck className="w-3.5 h-3.5" />Shipments ({shipments.length})</h3>
                  <div className="space-y-2">
                    {shipments.map((s) => (
                      <div key={s.id} data-testid={`shipment-${s.shipment_number}`} className="border border-slate-200 rounded-lg px-3 py-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono font-bold text-sm text-emerald-700">{s.shipment_number}</span>
                          {s.name && <span className="text-xs font-semibold text-slate-700">"{s.name}"</span>}
                          <span className="text-[11px] text-slate-500">{s.part_count} part(s)</span>
                          <span className="ml-auto text-[10px] text-slate-400 font-mono">{s.shipped_by} · {new Date(s.shipped_at).toLocaleString()}</span>
                        </div>
                        <div className="text-[11px] text-slate-500 mt-1 font-mono">{s.parts.map((p) => p.short_code).join(", ")}</div>
                        <div className="flex items-center gap-2 flex-wrap mt-1">
                          {s.handed_to
                            ? <span data-testid={`shipment-handover-${s.shipment_number}`} className="inline-flex items-center gap-1 text-[11px] font-semibold text-sky-800 bg-sky-50 border border-sky-200 rounded px-1.5 py-0.5">
                                <HandMetal className="w-3 h-3" />Handed to {s.handed_to}{s.handover_note ? ` — ${s.handover_note}` : ""}
                              </span>
                            : <span data-testid={`shipment-nohandover-${s.shipment_number}`} className="text-[11px] text-slate-400">no handover recorded</span>}
                          <button data-testid={`shipment-ticket-${s.shipment_number}`}
                            onClick={() => openTicket(sel, sel.assignment_type === "build" ? "build-ticket" : "parts-ticket", s.shipment_number)}
                            className="ml-auto inline-flex items-center gap-1 text-[11px] font-bold text-slate-600 hover:text-[#B01E28] underline">
                            <Receipt className="w-3 h-3" />This shipment's ticket
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(sel.corrections || []).length > 0 && (
                <div className="mt-4 pt-3 border-t border-amber-200" data-testid="cart-corrections">
                  <h3 className="text-xs font-bold uppercase text-amber-700 mb-2 flex items-center gap-1"><Undo2 className="w-3.5 h-3.5" />Corrections after sign-off ({sel.corrections.length})</h3>
                  <p className="text-[11px] text-slate-500 mb-2">This order stays as it was signed off. These are dated corrections made to parts afterward, recorded here so the connection isn't lost.</p>
                  <div className="space-y-2">
                    {sel.corrections.map((cx, i) => (
                      <div key={i} data-testid={`cart-correction-${cx.short_code}`} className="border border-amber-200 bg-amber-50/60 rounded-lg px-3 py-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono font-bold text-sm text-[#B01E28]">{cx.short_code}</span>
                          <span className="text-xs font-semibold text-slate-700">returned to stock</span>
                          {cx.reason_label && <span className="text-[11px] rounded-md bg-white border border-amber-200 px-1.5 py-0.5 text-amber-800">{cx.reason_label}</span>}
                          {cx.reversal && <span className="text-[11px] text-slate-500">reversed {cx.reversal.credit_type}{cx.reversal.value ? ` ${money(cx.reversal.value)}` : ""}</span>}
                          <span className="ml-auto text-[10px] text-slate-400 font-mono">{cx.by} · {cx.at ? new Date(cx.at).toLocaleString() : ""}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <Dialog open={shipSheet} onOpenChange={(v) => !v && setShipSheet(false)}>
        <DialogContent className="rounded-xl border-slate-200 sm:max-w-md" data-testid="ship-confirm-sheet">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><Truck className="w-5 h-5 text-emerald-600" />Send {shipSel.length} part(s) now?</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-500">This <b>leaves the yard</b> as one shipment. The rest of the cart stays open under the same PO. This can't be undone.</p>
            <div>
              <label className="text-xs font-bold uppercase text-slate-400">Shipment name (optional)</label>
              <input data-testid="ship-name-input" value={shipName} onChange={(e) => setShipName(e.target.value)} placeholder="e.g. Urgent doser" className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
            </div>
            {/* THE HANDOVER STAMP — who physically took it. Optional, never a blocker, but once
                it's typed it is on the shipment and on the ticket for good. */}
            <div className="rounded-lg border border-sky-200 bg-sky-50/60 p-2.5">
              <label className="text-xs font-bold uppercase text-sky-800 flex items-center gap-1"><HandMetal className="w-3.5 h-3.5" />Handed to (optional)</label>
              <input data-testid="ship-handed-to" value={handedTo} onChange={(e) => setHandedTo(e.target.value)}
                placeholder="Driver, customer or shop — who took it" className="mt-1 w-full border border-sky-200 rounded-lg px-3 py-2 text-sm" />
              <input data-testid="ship-handover-note" value={handNote} onChange={(e) => setHandNote(e.target.value)}
                placeholder="Note (optional) — truck #, time, signed for by…" className="mt-1.5 w-full border border-sky-200 rounded-lg px-3 py-2 text-sm" />
              <p className="text-[11px] text-sky-800/70 mt-1">Leave it blank if nobody took it in person — the send still works.</p>
            </div>
            <div className="max-h-40 overflow-y-auto border border-slate-100 rounded-lg divide-y divide-slate-100">
              {shipSel.map((pid) => {
                const l = (sel?.lines || []).find((x) => x.part_id === pid);
                return <div key={pid} className="px-3 py-1.5 text-sm font-mono">{l?.short_code} <span className="text-slate-400 text-xs">{l?.name}</span></div>;
              })}
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-lg" onClick={() => setShipSheet(false)} data-testid="ship-cancel">Cancel</Button>
            <Button data-testid="ship-confirm-send" disabled={shipping} className="rounded-lg bg-emerald-600 hover:bg-emerald-700 font-bold" onClick={() => doShip()}>{shipping ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Truck className="w-4 h-4 mr-1" />}SEND NOW</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ONE PO = ONE JOB — asked before a second job is started. YES is the default button,
          because adding to the job that is already open is nearly always what he means. */}
      <Dialog open={!!openJobAsk} onOpenChange={(v) => !v && setOpenJobAsk(null)}>
        <DialogContent className="rounded-xl border-slate-200 sm:max-w-md" data-testid="open-job-ask-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2 text-[#B45309]"><ClipboardList className="w-5 h-5" />This already has an open job — add to it?</DialogTitle>
            <DialogDescription data-testid="open-job-ask-which">
              {openJobAsk ? <><b>{openJobAsk.name || assignmentShort(openJobAsk)}</b> ({openJobAsk.cart_number}) is open with {openJobAsk.line_count} part{openJobAsk.line_count === 1 ? "" : "s"}{openJobAsk.status ? ` · ${openJobAsk.status.replace("_", " ")}` : ""}. One PO is one job — many shipments, one grand total.</> : ""}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:justify-between">
            <Button variant="outline" className="rounded-lg h-11 border-slate-300 text-slate-600"
              data-testid="open-job-ask-no" onClick={() => { setOpenJobAsk(null); createCart(true); }}>
              No — start a separate job
            </Button>
            <Button autoFocus data-testid="open-job-ask-yes" className="rounded-lg h-11 bg-emerald-600 hover:bg-emerald-700 font-bold"
              onClick={() => { const c = openJobAsk; setOpenJobAsk(null); setCreating(false); openCart(c.id); toast.success(`Adding to ${c.cart_number}`); }}>
              <CheckCircle2 className="w-5 h-5 mr-1.5" />Yes — add to it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PUTTING TWO JOBS TOGETHER — never without this confirm, and it says exactly what moves. */}
      <Dialog open={!!mergeAsk} onOpenChange={(v) => !v && setMergeAsk(null)}>
        <DialogContent className="rounded-xl border-slate-200 sm:max-w-md" data-testid="merge-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2 text-[#B45309]"><Merge className="w-5 h-5" />Put these jobs together?</DialogTitle>
            <DialogDescription data-testid="merge-confirm-what">
              {mergeAsk ? <>Every part on {mergeAsk.from.map((c) => c.cart_number).join(" and ")} moves onto <b>{mergeAsk.into.cart_number}</b> — {mergeAsk.into.name || assignmentShort(mergeAsk.into)}. The emptied job{mergeAsk.from.length === 1 ? " is" : "s are"} then closed. Prices and tags come with the parts.</> : ""}
            </DialogDescription>
          </DialogHeader>
          {mergeAsk && (
            <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 text-sm" data-testid="merge-confirm-list">
              <div className="px-3 py-2 bg-emerald-50"><b className="font-mono">{mergeAsk.into.cart_number}</b> keeps everything · {mergeAsk.into.line_count} part(s) now</div>
              {mergeAsk.from.map((c) => (
                <div key={c.id} className="px-3 py-2 text-slate-600"><b className="font-mono">{c.cart_number}</b> → moves {c.line_count} part(s) over, then closes</div>
              ))}
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-lg h-11" data-testid="merge-cancel" onClick={() => setMergeAsk(null)}>Leave them separate</Button>
            <Button data-testid="merge-confirm" disabled={merging} className="rounded-lg h-11 bg-[#E0781A] hover:bg-[#C76714] font-bold" onClick={doMerge}>
              {merging ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Merge className="w-4 h-4 mr-1" />}Yes — put them together
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* RENAME — a job's name is a person's words, so only a person changes it. */}
      <Dialog open={renaming} onOpenChange={(v) => !v && setRenaming(false)}>
        <DialogContent className="rounded-xl border-slate-200 sm:max-w-md" data-testid="rename-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Name this job</DialogTitle>
            <DialogDescription>What should everyone call it? Clear beats clever — the name is on the ticket.</DialogDescription>
          </DialogHeader>
          <input data-testid="rename-input" value={renameTo} onChange={(e) => setRenameTo(e.target.value)} autoFocus
            className="w-full border border-slate-200 rounded-lg px-3 py-3 text-base font-semibold" placeholder="e.g. Unit 4411 cab rebuild" />
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-lg h-11" data-testid="rename-cancel" onClick={() => setRenaming(false)}>Cancel</Button>
            <Button data-testid="rename-save" className="rounded-lg h-11 bg-[#B01E28] hover:bg-[#8f1820] font-bold" onClick={doRename}>Save the name</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Discard-draft confirmation — hard to undo (releases reservations), styled not browser prompt */}
      <Dialog open={discardConfirm} onOpenChange={setDiscardConfirm}>
        <DialogContent className="rounded-xl border-slate-200 sm:max-w-md" data-testid="discard-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><Trash2 className="w-5 h-5 text-[#B01E28]" />Discard this draft?</DialogTitle>
            <DialogDescription>{sel ? assignmentShort(sel) : ""} will be deleted and any reserved parts released back to Available. This can't be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-lg" onClick={() => setDiscardConfirm(false)} data-testid="discard-cancel">Keep draft</Button>
            <Button data-testid="discard-confirm-btn" className="rounded-lg bg-[#B01E28] hover:bg-red-800 font-bold" onClick={discardDraft}><Trash2 className="w-4 h-4 mr-1" />Discard</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sign-off confirmation — the most FINAL action: spell out the commitment, don't just say "sure?" */}
      <Dialog open={signoffConfirm} onOpenChange={setSignoffConfirm}>
        <DialogContent className="rounded-xl border-slate-200 sm:max-w-lg" data-testid="signoff-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-emerald-600" />Sign off {sel ? assignmentShort(sel) : ""} — this closes the order</DialogTitle>
            <DialogDescription asChild>
              <div className="text-sm text-slate-600 space-y-2 pt-1">
                <div>By signing off you confirm:</div>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Every part is <b>verified used</b> — installed on the vehicle, or already returned to the warehouse.</li>
                  <li>Retail <b>pricing is confirmed</b>.</li>
                  <li>All <b>returns have been processed</b> during this review.</li>
                </ul>
                <div className="text-[#B01E28] font-semibold">Once signed off the order is CLOSED — it can't be reopened and parts can't be returned from it.</div>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-lg" onClick={() => setSignoffConfirm(false)} data-testid="signoff-cancel">Not yet — keep reviewing</Button>
            <Button data-testid="signoff-confirm-btn" className="rounded-lg bg-emerald-600 hover:bg-emerald-700 font-bold" onClick={() => { setSignoffConfirm(false); signOff(sel.id); }}><ShieldCheck className="w-4 h-4 mr-1" />Sign off &amp; close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Return confirmation — money-reversal, tablet-friendly (no browser dialog) */}
      <Dialog open={!!returnConfirm} onOpenChange={(v) => !v && setReturnConfirm(null)}>
        <DialogContent className="rounded-xl border-slate-200 sm:max-w-md" data-testid="return-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><Undo2 className="w-5 h-5 text-sky-600" />Return {returnConfirm?.ids?.length || 0} shipped part(s)?</DialogTitle>
            <DialogDescription>
              This <b>reverses the sale</b> (the recovery credit is backed out) and drops each part into the <b>Put Away queue</b> so it can be re-binned. Only shipped lines are affected.
            </DialogDescription>
          </DialogHeader>
          <div className="py-1">
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2">Why did it come back?</div>
            <div className="flex flex-wrap gap-2">
              {RETURN_REASONS.map((r) => (
                <button key={r.code} data-testid={`return-reason-${r.code}`} onClick={() => setReturnReason(r.code)}
                  className={`rounded-full px-4 py-2.5 font-semibold text-sm border-2 transition-colors ${returnReason === r.code ? "border-sky-600 bg-sky-50 text-sky-700" : "border-slate-200 text-slate-600 hover:border-sky-500"}`}>
                  {r.label}
                </button>
              ))}
            </div>
            {returnReason === "other" && (
              <Textarea data-testid="return-reason-note" autoFocus value={returnNote} onChange={(e) => setReturnNote(e.target.value)}
                placeholder="Briefly, what happened?" className="mt-3 rounded-lg" rows={2} />
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-lg" onClick={() => setReturnConfirm(null)} data-testid="return-cancel">Cancel</Button>
            <Button data-testid="return-confirm-btn" disabled={returning || !returnReason || (returnReason === "other" && returnNote.trim().length < 2)} className="rounded-lg bg-sky-600 hover:bg-sky-700 font-bold" onClick={confirmReturn}>{returning ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Undo2 className="w-4 h-4 mr-1" />}Return to stock</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ONE BOX FOR A SEND: every missing bin scan, tag and photo of every part, one reason, one tap. */}
      <SendReasonDialog ask={shipAsk} busy={shipping} onSend={(r) => doShip(r)} onClose={() => setShipAsk(null)} />

      {/* GOING ON THE JOB WITHOUT ITS TAG OR PHOTO — never held up: alert, then the logged reason. */}
      <NoPhotoBypassDialog block={noPhoto} onClose={() => setNoPhoto(null)} actionLabel="Add to this job — logged"
        after="The photo is still needed before the job is signed off."
        onConfirm={async ({ reason, note }) => { const p = noPhoto.payload; setNoPhoto(null);
          await tryAdd({ ...p, floor_override: true, floor_override_reason: reason, floor_override_note: note }); }} />

      <Dialog open={!!shortPickL} onOpenChange={(v) => { if (!v) { setShortPickL(null); setShortReason(""); } }}>
        <DialogContent className="rounded-xl border-slate-200 sm:max-w-lg" data-testid="short-pick-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-[#B01E28]" />Can't find {shortPickL?.short_code}?</DialogTitle>
            <DialogDescription>This leaves the line unfilled and releases the part. Give a reason — it's recorded on the cart.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2">
              {SHORT_PRESETS.map((r) => (
                <button key={r} data-testid={`short-preset-${r.slice(0, 6)}`} onClick={() => setShortReason(r)}
                  className={`rounded-full px-3 py-2 text-sm font-semibold border transition-colors ${shortReason === r ? "bg-[#B01E28] text-white border-[#B01E28]" : "bg-white border-slate-300 hover:border-red-400"}`}>{r}</button>
              ))}
            </div>
            <Textarea data-testid="short-reason-input" value={shortReason} onChange={(e) => setShortReason(e.target.value)} placeholder="Or type a reason (min 3 characters)…" className="rounded-lg min-h-[60px] text-base" />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-lg h-12 text-base" onClick={() => { setShortPickL(null); setShortReason(""); }}>Cancel</Button>
            <Button data-testid="short-confirm" disabled={shortBusy || shortReason.trim().length < 3} className="rounded-lg h-12 text-base bg-[#B01E28] hover:bg-[#8f1820] font-bold" onClick={submitShortPick}>
              {shortBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <AlertTriangle className="w-4 h-4 mr-1" />}Flag short
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={voidConfirm} onOpenChange={(v) => !v && setVoidConfirm(false)}>
        <DialogContent className="rounded-xl border-slate-200 sm:max-w-md" data-testid="void-cart-confirm">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-[#B01E28]" />Void this cart?</DialogTitle>
            <DialogDescription>Are you sure? This <b>empties the cart</b>, releases its reserved parts back to stock, and <b>can't be undone</b>.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="rounded-lg border-slate-200" onClick={() => setVoidConfirm(false)}>Keep cart</Button>
            <Button data-testid="void-cart-confirm-yes" disabled={voiding} onClick={doVoid} className="rounded-lg bg-[#B01E28] hover:bg-[#8f1820] text-white">{voiding ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Trash2 className="w-4 h-4 mr-1.5" />Yes, void cart</>}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!otherWarn} onOpenChange={(v) => !v && setOtherWarn(null)}>
        <DialogContent className="rounded-xl border-slate-200 sm:max-w-md" data-testid="other-cart-warn">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-500" />
              {otherWarn?.kind === "reserved" ? "Reserved to another cart" : "Already on another cart"}</DialogTitle>
            <DialogDescription>
              {otherWarn?.kind === "reserved"
                ? <><b>{otherWarn?.short_code}</b> is reserved to <b>{otherWarn?.cart?.display || "another cart"}</b>. {otherWarn?.onConfirm ? "Release it from that cart and add it here?" : "It must be released before it can be added (needs approve-cart permission)."}</>
                : <><b>{otherWarn?.short_code}</b> is already on {(otherWarn?.carts || []).map((c) => c.display).join(", ")}. Add it here anyway? Only one cart can reserve it at approval.</>}
            </DialogDescription>
          </DialogHeader>
          <div className="text-xs">
            {(otherWarn?.kind === "reserved" ? (otherWarn?.cart ? [otherWarn.cart] : []) : (otherWarn?.carts || [])).map((c) => (
              <Link key={c.id} to={`/pull-carts?open=${c.id}`} onClick={() => setOtherWarn(null)} className="underline text-slate-600 inline-flex items-center gap-1 mr-3"><ExternalLink className="w-3 h-3" />View {c.cart_number}</Link>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-lg border-slate-200" onClick={() => setOtherWarn(null)}>Cancel</Button>
            {otherWarn?.onConfirm && (
              <Button data-testid="other-cart-add-anyway" className="rounded-lg bg-amber-600 hover:bg-amber-700 text-white"
                onClick={async () => { const fn = otherWarn.onConfirm; setOtherWarn(null); if (fn) await fn(); }}>
                {otherWarn?.kind === "reserved" ? "Release & add here" : "Add anyway"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!signBlock} onOpenChange={(v) => !v && setSignBlock(null)}>
        <DialogContent className="rounded-xl border-slate-200 sm:max-w-md" data-testid="sign-off-block">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-[#B01E28]" />Can't sign off yet</DialogTitle>
            <DialogDescription>
              <b>{signBlock?.count} sold part(s)</b> still need a sale price. Until they're priced, that cash won't flow to the source donor's recovery — so the cart can't be signed off. Price them in the Pricing Queue.
            </DialogDescription>
          </DialogHeader>
          <div className="text-xs font-mono text-slate-600 space-y-0.5 max-h-40 overflow-y-auto">
            {(signBlock?.parts || []).map((p) => <div key={p.part_id}>· {p.short_code} <span className="text-slate-400">{p.name}</span></div>)}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-lg border-slate-200" onClick={() => setSignBlock(null)}>Close</Button>
            <Button data-testid="sign-off-block-goto-queue" className="rounded-lg bg-[#B01E28] hover:bg-[#8f1820] text-white"
              onClick={() => { setSignBlock(null); setShowQueue(true); loadQueue(); }}>
              <DollarSign className="w-4 h-4 mr-1" />Open Pricing Queue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SIGN-OFF IS STRICT — his rule, 2026-10-17: *"A job cannot be signed off until every part
          on it has its source, its tag and its photo."* No override here; it names each part and
          exactly what it's missing so the gap can be closed. */}
      <Dialog open={!!notReadyBlock} onOpenChange={(v) => !v && setNotReadyBlock(null)}>
        <DialogContent className="rounded-xl border-slate-200 sm:max-w-lg" data-testid="sign-off-not-ready">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-[#B01E28]" />Can't sign off — {notReadyBlock?.count} part(s) aren't traceable yet</DialogTitle>
            <DialogDescription>
              Every part on a job needs its <b>source</b>, its <b>tag</b> and a <b>photo</b> before the foreman can close it. A part may <i>leave</i> without a tag or photo with a logged reason — but the job stays open until it's fixed. <b>There is no override here.</b>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5 max-h-60 overflow-y-auto" data-testid="sign-off-not-ready-list">
            {(notReadyBlock?.parts || []).map((p) => (
              <button key={p.part_id} type="button" data-testid={`sign-off-not-ready-${p.short_code}`}
                onClick={() => navigate(`/parts/${p.part_id}`)}
                className="w-full text-left rounded-lg border border-red-100 bg-red-50 px-3 py-2 hover:bg-red-100 transition-colors">
                <div className="font-mono font-bold text-sm text-[#B01E28]">{p.short_code}</div>
                <div className="text-xs text-slate-600 truncate">{p.name}</div>
                <div className="text-xs font-semibold text-[#B01E28] mt-0.5">Still needs: {(p.missing || []).join(", ")}</div>
              </button>
            ))}
          </div>
          <DialogFooter>
            <Button data-testid="sign-off-not-ready-close" variant="outline" className="rounded-lg border-slate-200" onClick={() => setNotReadyBlock(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Field-add confirm — deliberate "ship now, complete later" for a staging part */}
      <Dialog open={!!fieldAdd} onOpenChange={(v) => !v && setFieldAdd(null)}>
        <DialogContent className="rounded-xl border-slate-200 sm:max-w-md" data-testid="field-add-confirm">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><ClipboardList className="w-5 h-5 text-amber-600" />Field-add {fieldAdd?.short_code}?</DialogTitle>
            <DialogDescription>
              This part isn't fully identified yet, but it has a <b>source tie + photos</b> so it's traceable. Field-add it now to keep the floor moving — it'll ship, then land in <b>Needs Completion</b> for the foreman to finish. The cart can't be signed off until it's complete.
            </DialogDescription>
          </DialogHeader>
          {!!fieldAdd?.gaps?.length && (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">Still needs: {fieldAdd.gaps.join(", ")}</div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-lg border-slate-200" onClick={() => setFieldAdd(null)}>Cancel</Button>
            <Button data-testid="field-add-confirm-go" className="rounded-lg bg-amber-600 hover:bg-amber-700 text-white"
              onClick={async () => { const fn = fieldAdd.onConfirm; setFieldAdd(null); if (fn) await fn(); }}>
              <ClipboardList className="w-4 h-4 mr-1" />Field-add (complete later)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sign-off blocked on incomplete field-added parts */}
      <Dialog open={!!completionBlock} onOpenChange={(v) => !v && setCompletionBlock(null)}>
        <DialogContent className="rounded-xl border-slate-200 sm:max-w-md" data-testid="completion-block">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-600" />Can't sign off yet</DialogTitle>
            <DialogDescription>
              <b>{completionBlock?.count} field-added part(s)</b> still need full identification. Complete them from the photos before signing off.
            </DialogDescription>
          </DialogHeader>
          <div className="text-xs font-mono text-slate-600 space-y-0.5 max-h-40 overflow-y-auto">
            {(completionBlock?.parts || []).map((p) => <div key={p.part_id}>· {p.short_code} <span className="text-slate-400">needs {(p.gaps || []).join(", ")}</span></div>)}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-lg border-slate-200" onClick={() => setCompletionBlock(null)}>Close</Button>
            <Button data-testid="completion-block-goto-queue" className="rounded-lg bg-amber-600 hover:bg-amber-700 text-white"
              onClick={() => { setCompletionBlock(null); setShowCompletion(true); loadCompletion(); }}>
              <ClipboardList className="w-4 h-4 mr-1" />Open Needs Completion
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
